
import React, { useContext, useEffect, useState, useRef, Fragment, useCallback, useMemo, useLayoutEffect } from "react";
import axios from "axios";
import {
  ArrowDown,
  ArrowUp,
  Bot,
  Check,
  CircleCheck,
  CircleDot,
  CircleAlert,
  Clock4,
  Filter,
  Hand,
  HardHat,
  GripVertical,
  History,
  Building2,
  FolderOpen,
  MapPin,
  PauseCircle,
  Pencil,
  Play,
  Search,
  Shield,
  TriangleAlert,
  Upload,
  UserCheck,
  X,
  Zap,
} from "lucide-react";
import AppContext from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";
import { fetchAccountHistory, fetchFieldAgentMap, fetchPdfDataTaskList, fetchTaskListColumns, searchTasks, taskHistoryDetailsByDept, TASK_LIST_CODES, TaskListSearchError, updateTaskPriority } from "../../service/Task";
import {
  canonicalColumnKey,
  countActiveColumnFilters,
  isMeaningfulColumnFilter,
  makeBetweenFilter,
  makeContainsFilter,
  makeInFilter,
  MAX_COLUMN_FILTERS,
  MAX_PAGE_SIZE,
  serializeColumnFilters,
  TEXT_FILTER_DEBOUNCE_MS,
  toColumnConditions,
  toColumnKey,
} from "../../utils/taskListFilters";
import { createPortal } from "react-dom";
import { Spinner } from "../../Spinner";
import translations from "../common/Translations";
import AlertModal, { useAlertModal } from "../common/AlertModal";
import ReactSelector from "../common/CommonReactSelector";
import PageSizeSelector from "../common/PageSizeSelector";
import HoverTooltip from "../common/HoverTooltip";
import SharedOverflowTooltipText from "../common/OverflowTooltipText";
import ColumnValueFilterList from "../common/ColumnValueFilterList";
import Cookies from "js-cookie";
import { components } from 'react-select';
import moment from "moment";
import Modal from "../modals/Modal";
import { Dialog, Transition } from "@headlessui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TaskDetailPage from "../../pages/task/TaskDetailPage";
import AgentChatWidget from "../common/AgentChatWidget";
import { canShowActionColumn, getPermissionByScreenCode } from "../../utils/permissions";
import { normalizeAccountHistoryValue } from "../../utils/accountHistory";
import useDirection from "../../hooks/useDirection";
import ActionIconButton from "../common/ActionIconButton";
import LocationMapModal, { hasValidLocation } from "../common/LocationMapModal";
import ModalCloseButton from "../common/ModalCloseButton";
import PagerArrowButton from "../common/PagerArrowButton";
import {
  getCurrentUserType,
  getCurrentUserGroupIds,
  loadGroupsForDepartment,
  getGroupMemberIdSet,
  isTaskVisibleForGroupAccess,
} from "./GroupAssignParts";

const TASK_MANAGE_TABLE_FILTERS_KEY = "taskManageTableFilters";

/* The Assigned To column, under either spelling the backend uses for it
   ("assignedTo" in the grid, "Assignee Names" in /list/columns). It is the one
   column that is NOT filtered through columnFilters: the API takes assignee IDs
   in `assigneeIds`, so the funnel stores IDs and they are lifted out of the
   column-filter model on the way to the request. */
const ASSIGNED_TO_CANONICAL_KEYS = new Set(["assignedto", "assigneenames"]);
const isAssignedToColumnKey = (key) =>
  ASSIGNED_TO_CANONICAL_KEYS.has(
    String(key ?? "").toLowerCase().replace(/[^a-z0-9]/g, "")
  );
const TASK_SEARCH_VALUE_KEY = "taskSearchValue";
const TASK_SEARCH_VALUE_BACKUP_KEY = "taskSearchValueBackup";
const TASK_AREA_CODE_VALUE_KEY = "taskAreaCodeValue";
const TASK_AREA_CODE_VALUE_BACKUP_KEY = "taskAreaCodeValueBackup";
const TASK_MANAGE_TABLE_LAYOUT_KEY = "taskManageTableLayout";
const DYNAMIC_FILTER_HIDDEN_CANONICAL_KEYS = new Set(["area", "tariff", "category"]);
const TASK_LOCATION_KEY = "taskLocation";

const readTaskManageTableLayout = () => {
  try {
    const raw = localStorage.getItem(TASK_MANAGE_TABLE_LAYOUT_KEY);
    if (!raw) return { colWidths: {}, colOrder: null };
    const parsed = JSON.parse(raw);
    const colWidths =
      parsed?.colWidths && typeof parsed.colWidths === "object" && !Array.isArray(parsed.colWidths)
        ? Object.fromEntries(
            Object.entries(parsed.colWidths)
              .map(([key, width]) => [key, Number(width)])
              .filter(([key, width]) => key && Number.isFinite(width) && width >= 70)
          )
        : {};
    const colOrder = Array.isArray(parsed?.colOrder)
      ? parsed.colOrder.filter((key) => typeof key === "string" && key)
      : null;
    return { colWidths, colOrder: colOrder?.length ? colOrder : null };
  } catch {
    return { colWidths: {}, colOrder: null };
  }
};

/* The search endpoint matches `source` case-sensitively on the stored form
   ("Imported", not "IMPORTED"), so filter values are normalised to that shape --
   including legacy all-caps values still sitting in localStorage. */
const normalizeSourceFilterValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "Everything";
  if (raw.toLowerCase() === "everything") return "Everything";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const normalizePriorityFilterValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "Everything";
  if (raw.toLowerCase() === "everything") return "Everything";
  if (raw.toLowerCase() === "priority") return "Everything";
  return raw;
};

const normalizeTaskStatusFilterValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "Everything";
  if (raw.toLowerCase() === "everything") return "Everything";
  if (raw.toLowerCase() === "status") return "Everything";
  return raw;
};

const readTaskManageFilters = () => {
  try {
    const raw = sessionStorage.getItem(TASK_MANAGE_TABLE_FILTERS_KEY);
    if (raw) return JSON.parse(raw);
    const rawLocal = localStorage.getItem(TASK_MANAGE_TABLE_FILTERS_KEY);
    return rawLocal ? JSON.parse(rawLocal) : {};
  } catch {
    return {};
  }
};

const readMapListFiltersCookie = () => {
  try {
    const raw = Cookies.get("mapListFilters");
    const parsed = raw ? JSON.parse(raw) : {};
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const persistTaskManageFiltersPatch = (patch) => {
  try {
    const current = readTaskManageFilters();
    const next = { ...(current || {}), ...(patch || {}) };
    sessionStorage.setItem(TASK_MANAGE_TABLE_FILTERS_KEY, JSON.stringify(next));
    localStorage.setItem(TASK_MANAGE_TABLE_FILTERS_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event("taskManageFiltersChanged"));
  } catch (error) {
    console.error("Error persisting task table filters patch:", error);
  }
};

const persistMapListFiltersPatch = (patch) => {
  try {
    let mapListData = Cookies.get("mapListFilters");
    mapListData = mapListData ? JSON.parse(mapListData) : {};
    if (typeof mapListData !== "object" || mapListData === null) {
      mapListData = {};
    }
    Cookies.set("mapListFilters", JSON.stringify({ ...mapListData, ...(patch || {}) }));
  } catch (error) {
    console.error("Error updating mapListFilters cookie:", error);
  }
};

const persistTaskSearchValue = (value) => {
  try {
    const normalized = String(value ?? "").trim();
    if (normalized) {
      sessionStorage.setItem(TASK_SEARCH_VALUE_KEY, normalized);
      sessionStorage.setItem(TASK_SEARCH_VALUE_BACKUP_KEY, normalized);
      localStorage.setItem(TASK_SEARCH_VALUE_BACKUP_KEY, normalized);
      return;
    }
    sessionStorage.removeItem(TASK_SEARCH_VALUE_KEY);
    sessionStorage.removeItem(TASK_SEARCH_VALUE_BACKUP_KEY);
    localStorage.removeItem(TASK_SEARCH_VALUE_BACKUP_KEY);
  } catch (error) {
    console.error("Error persisting task search value:", error);
  }
};

const persistTaskAreaCodeValue = (value) => {
  try {
    const normalized = String(value ?? "").trim();
    if (normalized) {
      sessionStorage.setItem(TASK_AREA_CODE_VALUE_KEY, normalized);
      sessionStorage.setItem(TASK_AREA_CODE_VALUE_BACKUP_KEY, normalized);
      localStorage.setItem(TASK_AREA_CODE_VALUE_BACKUP_KEY, normalized);
      return;
    }
    sessionStorage.removeItem(TASK_AREA_CODE_VALUE_KEY);
    sessionStorage.removeItem(TASK_AREA_CODE_VALUE_BACKUP_KEY);
    localStorage.removeItem(TASK_AREA_CODE_VALUE_BACKUP_KEY);
  } catch (error) {
    console.error("Error persisting task area code value:", error);
  }
};

const priorityOptions = [
  {
    value: "Low_Priority",
    label: "Low",
    icon: <ArrowDown size={14} />,
    bg: "#eef4ff",
    border: "#bfd1ff",
    text: "#2563eb",
  },
  {
    value: "Medium_Priority",
    label: "Medium",
    icon: <ArrowUp size={14} />,
    bg: "#fff4e5",
    border: "#ffd8a5",
    text: "#f2930d",
  },
  {
    value: "High_Priority",
    label: "High",
    icon: <TriangleAlert size={14} />,
    bg: "#fef2f2",
    border: "#ffc9c9",
    text: "#dc2626",
  }
];

const CustomOption = (props) => {
  const { data, isSelected } = props;

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-2 text-[14px] leading-5 text-[#1f2937] py-[2px]">
        <span className="w-4 min-w-4 flex justify-center text-[#111827]">
          {isSelected ? <Check size={16} /> : null}
        </span>
        <span>{data.label}</span>
      </div>
    </components.Option>
  );
};

const PriorityDropdown = ({
  task,
  updateTaskPriorityHandler,
  isDisabled = false,
}) => {
  const normalizePriorityValue = (value) => {
    const map = {
      Low: "Low_Priority",
      Medium: "Medium_Priority",
      High: "High_Priority"
    };
    return map[value] || value;
  };

  const normalizedTaskPriority = normalizePriorityValue(task.priority);
  const selectedOption =
    priorityOptions.find((option) => option.value === normalizedTaskPriority) || null;

  return (
    <div className={`mx-auto max-w-[100px] min-w-[100px] ${isDisabled ? "cursor-not-allowed" : ""}`}>
      <ReactSelector
        value={selectedOption}
        options={priorityOptions}
        isDisabled={isDisabled}
        onChange={(nextOption) => {
          if (!nextOption || nextOption.value === normalizedTaskPriority) return;
          updateTaskPriorityHandler(task.taskId, nextOption.value);
        }}
        height="30px"
        menuPlacement="bottom"
        menuPortalTarget={document.body}
        placeholder={selectedOption?.label || "Select"}
        fontSize="11px"
        isSearchable={false}
        components={{ Option: CustomOption }}
        styles={{
          control: (base, state) => ({
            ...base,
            minHeight: "30px",
            height: "30px",
            borderRadius: "8px",
            border: `1px solid ${selectedOption?.border || "#e5e7eb"}`,
            background: selectedOption?.bg || "#ffffff",
            boxShadow: "none",
            fontWeight: 500,
            paddingLeft: "4px",
            paddingRight: "0px",
            opacity: state.isDisabled ? 0.9 : 1,
            cursor: state.isDisabled ? "not-allowed" : "pointer",
            pointerEvents: state.isDisabled ? "none" : "auto",
          }),
          valueContainer: (base) => ({
            ...base,
            padding: "0 5px",
            gap: "6px",
          }),
          singleValue: (base, state) => ({
            ...base,
            color: state?.data?.text || "#374151",
            fontSize: "11px",
            fontWeight: 500,
            margin: 0,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }),
          dropdownIndicator: (base) => ({
            ...base,
            color: "#9ca3af",
            padding: "4px 8px 4px 0",
          }),
          menu: (base) => ({
            ...base,
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 8px 24px rgba(17,24,39,0.14)",
            overflow: "hidden",
          }),
          menuList: (base) => ({
            ...base,
            paddingTop: "6px",
            paddingBottom: "6px",
          }),
          option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? "#f8fafc" : "#ffffff",
            color: "#111827",
            padding: "9px 12px",
          }),
        }}
        formatOptionLabel={(option, { context }) => {
          if (context === "menu") return option.label;
          return (
            <span className="inline-flex items-center gap-1.5">
              <span style={{ color: option.text }}>{option.icon}</span>
              <span>{option.label}</span>
            </span>
          );
        }}
      />
    </div>
  );
};

const HiddenColumnsPlaceholder = ({ theme }) => (
  <div
    className={`w-full max-w-[320px] rounded-xl border px-4 py-5 text-center shadow-sm ${
      theme === "dark"
        ? "border-gray-600 bg-gray-800/95"
        : "border-gray-200 bg-white/95"
    }`}
  >
    <div
      className={`mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full ${
        theme === "dark" ? "bg-gray-700 text-gray-200" : "bg-blue-50 text-blue-600"
      }`}
    >
      <CircleAlert size={18} />
    </div>
    <p className={`text-sm font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-800"}`}>
      More columns are hidden
    </p>
    <p className={`mt-1 text-xs leading-5 ${theme === "dark" ? "text-gray-300" : "text-gray-500"}`}>
      Use the Columns button to show additional columns.
    </p>
  </div>
);

const HiddenColumnsChip = ({ theme }) => (
  <div
    className={`inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-semibold shadow-sm ${
      theme === "dark"
        ? "border-gray-600 bg-gray-800/95 text-gray-100"
        : "border-gray-200 bg-white/95 text-[#475569]"
    }`}
    aria-hidden="true"
  >
    + Add columns
  </div>
);

const Placeholder = (props) => {
  return <components.Placeholder {...props} />;
};

const normalizeOverflowText = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

// Keeps the "-" fallback for empty values, then defers to the shared blue overflow tooltip.
const OverflowTooltipText = ({ text, className = "" }) => (
  <SharedOverflowTooltipText text={normalizeOverflowText(text)} className={className} />
);

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#16a34a", "#ea580c", "#db2777", "#4f46e5", "#0d9488"];
const avatarColorFor = (name) => AVATAR_COLORS[(name || "?").charCodeAt(0) % AVATAR_COLORS.length];

// Geometry of the hovered "field agents" popup (shared by the Task ID and
// Assign To cells) -- kept here so the placement math and the rendered box can
// never drift apart.
const AGENT_TOOLTIP_WIDTH = 260;
const AGENT_TOOLTIP_MAX_HEIGHT = 280;
// Small gap + generous close delay: the pointer has to leave the trigger and
// cross empty space to reach the popup, and it must survive that trip for the
// agent list to be scrollable.
const AGENT_TOOLTIP_GAP = 10;
const AGENT_TOOLTIP_EDGE_MARGIN = 8;
const AGENT_TOOLTIP_ENTRY_OFFSET = 28;
const AGENT_TOOLTIP_CLOSE_DELAY_MS = 320;

/** Assignees who actually completed the task (`completed: true`) come first so
 *  they stay inside the 2-avatar window instead of collapsing into "+N", and are
 *  marked with a green check badge / green name. */
const sortCompletedFirst = (assignees) =>
  [...assignees].sort((a, b) => Number(!!b?.completed) - Number(!!a?.completed));

const AssigneeAvatarStack = ({ assignees, size = 20, langMode = "en" }) => {
  const list = Array.isArray(assignees) ? assignees.filter((a) => a?.assigneeName) : [];
  if (!list.length) return <span className="text-slate-300">—</span>;
  const completedLabel = translations[langMode]?.assigneeCompleted || "Completed";
  const ordered = sortCompletedFirst(list);
  const shown = ordered.length > 2 ? ordered.slice(0, 2) : ordered;
  const overflow = ordered.length - shown.length;
  const badgeSize = Math.round(size * 0.5);
  // No native `title` anywhere in here on purpose: hovering the cell now opens
  // the full agent popup, and a browser tooltip would pop up on top of it.
  return (
    <div className="flex items-center gap-1.5 justify-start">
      <div className="flex -space-x-1.5 shrink-0">
        {shown.map((a, i) => (
          <div
            key={a.assigneeId ?? i}
            className={`relative shrink-0 ${a?.completed ? "z-[1]" : ""}`}
            style={{ width: size, height: size }}
          >
            <div
              className={`rounded-full flex items-center justify-center text-white font-medium border ${a?.completed ? "border-[#29a36a]" : "border-white"}`}
              style={{ background: avatarColorFor(a.assigneeName), width: size, height: size, fontSize: size * 0.42 }}
            >
              {String(a.assigneeName).trim().charAt(0).toUpperCase()}
            </div>
            {a?.completed && (
              <span
                role="img"
                aria-label={`${a.assigneeName} - ${completedLabel}`}
                className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full bg-[#29a36a] text-white border border-white"
                style={{ width: badgeSize, height: badgeSize }}
              >
                <Check size={Math.round(size * 0.34)} strokeWidth={4} />
              </span>
            )}
          </div>
        ))}
        {overflow > 0 && (
          <div
            className="rounded-full flex items-center justify-center text-slate-600 bg-slate-200 border border-white font-semibold shrink-0"
            style={{ width: size, height: size, fontSize: size * 0.4 }}
          >
            +{overflow}
          </div>
        )}
      </div>
      <span className="text-xs truncate max-w-[100px]">
        {ordered.map((a, i) => (
          <Fragment key={a.assigneeId ?? i}>
            {i > 0 && <span className="text-slate-400">, </span>}
            <span className={a?.completed ? "font-semibold text-[#29a36a]" : "text-slate-500"}>
              {a.assigneeName}
            </span>
          </Fragment>
        ))}
      </span>
    </div>
  );
};

/**
 * The "field agents" popup shown when hovering a row's Task ID or Assign To cell.
 *
 * Positioned in *viewport* coordinates (`position: fixed`) against the hovered
 * cell, and measured after mount so a short list (1-2 agents) is placed right
 * next to its row instead of being shoved up by the full max-height. It also
 * dismisses on any outside scroll: the anchor row slides under the sticky table
 * header, and a popup left behind would float over the header detached from the
 * row it belongs to.
 */
const AgentHoverPopup = ({ anchorEl, task, langMode, onMouseEnter, onMouseLeave, onDismiss }) => {
  const boxRef = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, visibility: "hidden" });
  // Which side of the anchor the popup sits on, and where its arrow tip lands along that edge.
  const [arrow, setArrow] = useState({ side: "right", top: 24 });

  const assignees = useMemo(() => sortCompletedFirst(task?.assignees || []), [task]);
  const completedNames = assignees.filter((a) => a?.completed).map((a) => a?.assigneeName).filter(Boolean);
  const completedLabel = translations[langMode]?.assigneeCompleted || "Completed";
  const completedByLabel = translations[langMode]?.assigneeCompletedBy || "Completed by";

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!anchorEl || !box) return;
    const rect = anchorEl.getBoundingClientRect();
    const height = Math.min(box.offsetHeight, AGENT_TOOLTIP_MAX_HEIGHT);

    const fitsRight =
      rect.right + AGENT_TOOLTIP_GAP + AGENT_TOOLTIP_WIDTH <= window.innerWidth - AGENT_TOOLTIP_EDGE_MARGIN;
    let left = fitsRight
      ? rect.right + AGENT_TOOLTIP_GAP
      : Math.max(AGENT_TOOLTIP_EDGE_MARGIN, rect.left - AGENT_TOOLTIP_WIDTH - AGENT_TOOLTIP_GAP);

    if (left + AGENT_TOOLTIP_WIDTH > window.innerWidth - AGENT_TOOLTIP_EDGE_MARGIN) {
      left = Math.max(AGENT_TOOLTIP_EDGE_MARGIN, window.innerWidth - AGENT_TOOLTIP_WIDTH - AGENT_TOOLTIP_EDGE_MARGIN);
    }

    // Opened a little above the anchor's middle so a pointer moving straight
    // across lands inside the body of the popup, not on its top edge.
    const preferredTop = rect.top + rect.height / 2 - AGENT_TOOLTIP_ENTRY_OFFSET;
    const maxTop = window.innerHeight - height - AGENT_TOOLTIP_EDGE_MARGIN;
    const top = Math.max(AGENT_TOOLTIP_EDGE_MARGIN, Math.min(preferredTop, Math.max(AGENT_TOOLTIP_EDGE_MARGIN, maxTop)));

    setStyle({ top, left, visibility: "visible" });
    // Arrow points at the middle of the anchor, kept clear of the rounded corners.
    setArrow({
      side: fitsRight ? "right" : "left",
      top: Math.min(Math.max(rect.top + rect.height / 2 - top, 16), Math.max(16, height - 16)),
    });
  }, [anchorEl, assignees.length]);

  useEffect(() => {
    let timeoutId;
    const handleOutsideInteraction = (event) => {
      const target = event?.target;
      if (!target || !(target instanceof Node)) return;
      if (
        boxRef.current &&
        !boxRef.current.contains(target) &&
        anchorEl &&
        !anchorEl.contains(target)
      ) {
        onDismiss?.();
      }
    };
    const handleOutsideScroll = (event) => {
      const target = event?.target;
      // Scrolling inside the agent popup itself must not dismiss it.
      if (target && target instanceof Node && boxRef.current && boxRef.current.contains(target)) {
        return;
      }
      onDismiss?.();
    };

    // Delay attaching listeners slightly so the click/tap that opened this popup does not immediately close it
    timeoutId = setTimeout(() => {
      document.addEventListener("pointerdown", handleOutsideInteraction);
      window.addEventListener("scroll", handleOutsideScroll, true);
      window.addEventListener("resize", handleOutsideScroll);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("pointerdown", handleOutsideInteraction);
      window.removeEventListener("scroll", handleOutsideScroll, true);
      window.removeEventListener("resize", handleOutsideScroll);
    };
  }, [anchorEl, onDismiss]);

  if (!task) return null;

  return createPortal(
    <div
      ref={boxRef}
      className="fixed z-[9999999] rounded-xl border border-slate-200 bg-white shadow-xl"
      style={{ ...style, width: AGENT_TOOLTIP_WIDTH, maxWidth: "calc(100vw - 16px)", maxHeight: AGENT_TOOLTIP_MAX_HEIGHT }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Arrow lives outside the scrolling area below, which would otherwise clip it. */}
      <span
        aria-hidden="true"
        className={`absolute h-2.5 w-2.5 -translate-y-1/2 rotate-45 border border-slate-200 bg-white ${
          arrow.side === "right" ? "-left-[6px] border-r-0 border-t-0" : "-right-[6px] border-b-0 border-l-0"
        }`}
        style={{ top: arrow.top }}
      />
      <div
        className="overflow-y-auto overscroll-contain rounded-xl p-2"
        style={{ maxHeight: AGENT_TOOLTIP_MAX_HEIGHT }}
      >
      {assignees.length > 0 ? (
        <>
          <div className="flex items-center justify-between px-2 pb-1.5 pt-1">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {translations[langMode].fieldAgents || "Field Agents"} ({assignees.length})
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss?.();
              }}
              className="text-slate-400 hover:text-slate-600 rounded p-0.5 transition-colors"
              aria-label={translations[langMode]?.bulkExportClose ?? "Close"}
            >
              <X size={13} />
            </button>
          </div>
          {completedNames.length > 0 && (
            <div className="mx-2 mb-1.5 flex items-start gap-1.5 rounded-lg bg-[#e8f7f0] px-2 py-1.5 text-[11px] text-[#1f7d52]">
              <CircleCheck className="mt-[1px] shrink-0" size={13} />
              <span className="min-w-0">
                <span className="font-semibold">{completedByLabel}:</span> {completedNames.join(", ")}
              </span>
            </div>
          )}
          <div className="flex flex-col">
            {assignees.map((assignee) => (
              <div
                key={assignee.assigneeId}
                className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 ${assignee?.completed
                  ? "bg-[#e8f7f0]/60 hover:bg-[#e8f7f0]"
                  : "hover:bg-slate-50"
                  }`}
              >
                <div className="relative h-7 w-7 shrink-0">
                  <div
                    className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold text-white ${assignee?.completed ? "ring-2 ring-[#29a36a] ring-offset-1" : ""
                      }`}
                    style={{ background: avatarColorFor(assignee.assigneeName) }}
                  >
                    {String(assignee.assigneeName || "?").trim().charAt(0).toUpperCase()}
                  </div>
                  {assignee?.completed && (
                    <span className="absolute -bottom-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border border-white bg-[#29a36a] text-white">
                      <Check size={9} strokeWidth={4} />
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-xs font-medium ${assignee?.completed ? "text-[#1f7d52]" : "text-slate-700"}`}>
                    {assignee.assigneeName}
                  </p>
                  <p className="truncate text-[11px] text-slate-400">
                    {translations[langMode].FieldId}: {assignee.assigneeId}
                  </p>
                </div>
                {assignee?.completed && (
                  <span className="shrink-0 rounded-md bg-[#29a36a] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
                    {completedLabel}
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="px-2 py-3 text-center text-xs text-slate-400">
          {translations[langMode].NoasssingData}
        </div>
      )}
      </div>
    </div>,
    document.body
  );
};

/* Excel-style per-column filter popover: a funnel icon in the header that reveals
   the column's existing filter control in a portalled panel, instead of a
   permanently-visible second header row. */
// Roughly the panel's height with its 192px value list, search box and buttons.
const FILTER_POPOVER_PREFERRED_HEIGHT = 340;

const HeaderFilterPopover = ({ active, children, width = 232, tooltipText = "Filter" }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.min(Math.max(8, r.left - width + 20), window.innerWidth - width - 8);
    // Open below the funnel; when the window is too short for the panel, open above it instead
    // (or whichever side is roomier) and cap the height so it scrolls rather than running
    // off the screen.
    const spaceBelow = window.innerHeight - r.bottom - 6 - 8;
    const spaceAbove = r.top - 6 - 8;
    if (spaceBelow < FILTER_POPOVER_PREFERRED_HEIGHT && spaceAbove > spaceBelow) {
      setPos({ left, bottom: window.innerHeight - r.top + 6, maxHeight: spaceAbove });
    } else {
      setPos({ left, top: r.bottom + 6, maxHeight: spaceBelow });
    }
  };

  const openPanel = () => {
    place();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => place();
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const panel = open && pos ? createPortal(
    <div
      ref={popRef}
      onClick={(e) => e.stopPropagation()}
      className="fixed z-[10060] overflow-y-auto overscroll-contain bg-white border border-slate-200 rounded-lg shadow-xl normal-case font-normal tracking-normal p-3"
      style={{ left: pos.left, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight, width }}
    >
      {typeof children === "function" ? children(() => setOpen(false)) : children}
    </div>,
    document.body
  ) : null;

  return (
    <span className="relative inline-flex shrink-0">
      <HoverTooltip text={tooltipText}>
        <button
          ref={btnRef}
          type="button"
          onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : openPanel(); }}
          className={`inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors ${active ? "text-[#236DB4] bg-blue-50" : "text-[#236DB4] hover:bg-blue-50"}`}
        >
          <Filter size={12} fill={active ? "currentColor" : "none"} />
        </button>
      </HoverTooltip>
      {panel}
    </span>
  );
};

const taskStatusMeta = {
  Everything: {
    label: "All",
    text: "#64748b",
    bg: "#f1f5f9",
    dot: "#94a3b8",
  },
  Active: {
    label: "Active",
    text: "#f2930d",
    bg: "#fcf2e3",
    dot: "#f2930d",
  },
  Assigned: {
    label: "Assigned",
    text: "#3571e9",
    bg: "#e9effb",
    dot: "#3571e9",
  },
  On_Hold: {
    label: "On Hold",
    text: "#EF4A00",
    bg: "#EF4A0033",
    dot: "#EF4A00",
  },
  In_Progress: {
    label: "In Progress",
    text: "#FF9F29",
    bg: "#FF9F2933",
    dot: "#FF9F29",
  },
  Pending: {
    label: "Pending",
    text: "#838995",
    bg: "#eeeff1",
    dot: "#838995",
  },
  Completed: {
    label: "Completed",
    text: "#8541c8",
    bg: "#f2ecf9",
    dot: "#8541c8",
  },
  Finished: {
    label: "Finished",
    text: "#29a36a",
    bg: "#e8f7f0",
    dot: "#29a36a",
  },
};

const taskStatusFlatOptions = [
  { value: "Everything", label: "All" },
  { value: "Active", label: "Active" },
  { value: "Assigned", label: "Assigned" },
  { value: "On_Hold", label: "On Hold" },
  { value: "In_Progress", label: "In Progress" },
  { value: "Pending", label: "Pending" },
  { value: "Completed", label: "Completed" },
  { value: "Finished", label: "Finished" },
];

// Static filter option list — module-level so it keeps a stable reference
// across renders. It used to be redeclared as a new array literal inside the
// component on every render, which made a useEffect depending on it re-run on
// every re-render (see the "mapListFilters cookie hydration" effect below).
const PRIORITY_FILTER_OPTIONS = [
  { value: "Everything", label: "All" },
  { value: "Low_Priority", label: "Low Priority" },
  { value: "Medium_Priority", label: "Medium Priority" },
  { value: "High_Priority", label: "High Priority" },
];

const formatTaskStatusGroupLabel = (group) => (
  <div className="border-t border-[#2b2b2b] pt-3 pb-2 first:border-t-0 first:pt-0">
    <span className="text-xs font-semibold text-[#a1a1aa] !capitalize">{group.label}</span>
  </div>
);

const isDateColumn = (header) => {
  if (!header?.value) return false;
  const val = header.value.toLowerCase();
  const lbl = (header.label || "").toLowerCase();
  return val === "createddate" || val === "completeddate" || val.includes("date") || lbl.includes("date");
};

const TaskListView = ({
  selectedTasks, setSelectedTasks,
  setIsChecked,
  selectedtask,
  setselectedtask,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  setTotalPageSize,
  totalPageSize, setFinishedStatus,
  assigneesToUnassign, setAssigneesToUnassign,
  provideTasksWithMissingLatAndLong = false,
  setProvideTasksWithMissingLatAndLong,
  onTableHeadersChange,
  onSearchFieldsReady,
  visibleColumnKeys = [],
  aiSourceFilter,
  filterResetToken = 0,
  filtersHydrated = true,
  showTableFilters = true,
  onColumnAccessStateChange,
  onExportRequestChange,
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  // The hovered cell element the agent popup anchors itself to.
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const tooltipRef = useRef(null);
  const [hoveredTaskId, setHoveredTaskId] = useState(null);
  // Which cell opened the agent popup ("taskId" | "assignedTo") -- both columns
  // trigger the same popup, this keeps only one instance mounted at a time.
  const [hoveredTooltipSource, setHoveredTooltipSource] = useState(null);
  const [isPopupPinned, setIsPopupPinned] = useState(false);
  const tooltipCloseTimerRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [areainputValue, setAreaInputValue] = useState("");
  const [mobileNoinputValue, setMobileNoInputValue] = useState("");
  const [MobileNo, setMobileNo] = useState("");
  const [assignTaskAgent, setAssignTaskAgent] = useState(null);
  const [optionsTaskAgent, setOptionsTaskAgent] = useState([]);
  // Set of field-agent userIds the current user may see, derived from their accessible groups.
  // null = no group restriction (super admin, or a user with no assigned groups) => show all agents.
  const [groupAllowedAgentIds, setGroupAllowedAgentIds] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedPdfLink, setSelectedPdfLink] = useState("");
  const [selectedTaskForSlider, setSelectedTaskForSlider] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [accountHistory, setAccountHistory] = useState(null);
  const [taskIdFilter, setTaskIdFilter] = useState("");
  const [taskIdInputValue, setTaskIdInputValue] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("Everything");
  const [inputFilters, setInputFilters] = useState({});
  const [columnFilters, setColumnFilters] = useState({});
  const { alertProps, showAlert } = useAlertModal();
  // One-shot flag, true for ~900ms right after showTableFilters flips
  // false -> true, so the newly-revealed per-column funnel icons can pulse to
  // draw the eye -- clicking the toggle previously gave almost no feedback
  // about what changed, especially for columns off-screen to the right.
  const [justRevealedFilters, setJustRevealedFilters] = useState(false);
  const prevShowTableFiltersRef = useRef(showTableFilters);
  useEffect(() => {
    if (showTableFilters && !prevShowTableFiltersRef.current) {
      setJustRevealedFilters(true);
      const t = setTimeout(() => setJustRevealedFilters(false), 900);
      prevShowTableFiltersRef.current = showTableFilters;
      return () => clearTimeout(t);
    }
    prevShowTableFiltersRef.current = showTableFilters;
  }, [showTableFilters]);
  const [hasHydratedTableFilters, setHasHydratedTableFilters] = useState(false);
  // Monotonic request id. Filtering quickly against a slow query WILL return
  // out of order, so every response checks that it is still the newest before
  // touching state; the AbortController alone isn't enough, because a response
  // can already be in flight past the point where aborting has any effect.
  const searchRequestIdRef = useRef(0);
  const lastFetchedActivityRef = useRef(null);
  const [searchErrorCode, setSearchErrorCode] = useState(null);
  const [taskLocationView, setTaskLocationView] = useState(null);
  // Column width and drag order persist across refreshes and logout/login.
  // The toolbar Reset action clears both values back to the table defaults.
  const initialTableLayoutRef = useRef(null);
  if (initialTableLayoutRef.current === null) {
    initialTableLayoutRef.current = readTaskManageTableLayout();
  }
  const [colWidths, setColWidths] = useState(() => initialTableLayoutRef.current.colWidths);
  const [colOrderOverride, setColOrderOverride] = useState(() => initialTableLayoutRef.current.colOrder);
  const resizeColRef = useRef(null);
  const [dragColKey, setDragColKey] = useState(null);
  const [dragOverColKey, setDragOverColKey] = useState(null);
  const previousResetTokenRef = useRef(filterResetToken);
  const searchHydratedRef = useRef(false);
  const areaHydratedRef = useRef(false);
  const dynamicHeadersCacheRef = useRef([]);
  const [workflowColumns, setWorkflowColumns] = useState([]);
  const [workflowColumnsLoaded, setWorkflowColumnsLoaded] = useState(false);
  const showTaskActionColumn = canShowActionColumn("TASK_MANAGEMENT");
  const canUpdateTask = Boolean(getPermissionByScreenCode("TASK_MANAGEMENT")?.canUpdate);
  const hasAIChatAgentAccess = getPermissionByScreenCode("AI_CHAT_AGENT");
  const context = useContext(AppContext);
  const {
    workflowId,
    priority,
    active,
    setTaskData,
    taskData,
    taskListRefreshKey,
    bumpTaskListFetchEpoch,
    setSelectAll,
    selectAll,
    areaCode,
    accountNo,
    selectedUserId,
    setSelectedUserId,
    setAccountNo,
    // NOTE: selectedSubactivity is no longer read. The old get-tasks endpoint
    // took a `subActivity` query param; the list/search body has no equivalent
    // field, so it is not sent.
    selectedOrgId,
    setActive,
    setPriority,
    langMode,
    setAreaCode,
    setSelectedUserName,
    fieldAgentList,
    activityId,
    projectId,
    setRenderedTaskCount,
  } = context;
  const { isRTL: isRtl, dir } = useDirection();
  const { theme } = useContext(AppContext);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedTaskForSlider(null);
      }
    };
    if (selectedTaskForSlider) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedTaskForSlider]);

  useEffect(() => {
    if (selectedTaskForSlider) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedTaskForSlider]);

  useEffect(() => {
    try {
      const rawFilters = sessionStorage.getItem(TASK_MANAGE_TABLE_FILTERS_KEY);
      const cookieFilters = readMapListFiltersCookie();
      const cookieTableFilters = cookieFilters?.tableFilters;
      const rawLocalFilters = localStorage.getItem(TASK_MANAGE_TABLE_FILTERS_KEY);

      const parsedFilters = rawFilters
        ? JSON.parse(rawFilters)
        : rawLocalFilters
          ? JSON.parse(rawLocalFilters)
          : (cookieTableFilters && typeof cookieTableFilters === "object" ? cookieTableFilters : null);

      if (!parsedFilters) {
        setHasHydratedTableFilters(true);
        return;
      }
      setTaskIdFilter(parsedFilters?.taskIdFilter || "");
      setTaskIdInputValue(parsedFilters?.taskIdInputValue || "");
      setCreatedByFilter(parsedFilters?.createdByFilter || "");
      setSourceFilter(normalizeSourceFilterValue(parsedFilters?.sourceFilter));
      if (parsedFilters?.priorityFilter !== undefined) {
        setPriority(normalizePriorityFilterValue(parsedFilters?.priorityFilter));
      }
      if (parsedFilters?.taskStatusFilter !== undefined) {
        const normalizedTaskStatus = normalizeTaskStatusFilterValue(parsedFilters?.taskStatusFilter);
        setActive(normalizedTaskStatus);
        setFinishedStatus(normalizedTaskStatus);
      }
      const parsedInputFilters = parsedFilters?.inputFilters || {};
      const migratedInputFilters = { ...parsedInputFilters };

      // Backward compatibility: older builds persisted METER_TYPE under "meter type".
      if (migratedInputFilters["meter type"] && !migratedInputFilters["METER_TYPE"]) {
        migratedInputFilters["METER_TYPE"] = migratedInputFilters["meter type"];
      }
      delete migratedInputFilters["meter type"];

      const restoredColumnSearch = Object.entries(migratedInputFilters).find(
        ([column, value]) => column && String(value ?? "").trim()
      );

      setInputFilters(restoredColumnSearch ? { [restoredColumnSearch[0]]: restoredColumnSearch[1] } : {});

      // Persisted under a new key: the legacy `columnFilters` field held bare
      // arrays (value list or [from, to], ambiguous without knowing the column
      // type), whereas entries are now self-describing {column, op, ...}.
      // Anything stored by an older build is ignored rather than guessed at.
      const restoredModel = parsedFilters?.columnFilterModel;
      setColumnFilters(
        restoredModel && typeof restoredModel === "object"
          ? Object.fromEntries(
              Object.entries(restoredModel).filter(([, filter]) => isMeaningfulColumnFilter(filter))
            )
          : {}
      );
      if (parsedFilters?.assignTaskAgent !== undefined && parsedFilters?.assignTaskAgent !== null) {
        setAssignTaskAgent(parsedFilters.assignTaskAgent);
      }
    } catch (error) {
      console.error("Error restoring task table filters:", error);
    }
    setHasHydratedTableFilters(true);
  }, []);

  useEffect(() => {
    if (!hasHydratedTableFilters) return;

    try {
      const tableFiltersPayload = {
        taskIdFilter,
        taskIdInputValue,
        createdByFilter,
        sourceFilter: normalizeSourceFilterValue(sourceFilter),
        priorityFilter: normalizePriorityFilterValue(priority),
        taskStatusFilter: normalizeTaskStatusFilterValue(active),
        inputFilters,
        // Self-describing {column, op, ...} entries. The toolbar badge counts
        // these with countActiveColumnFilters so the drop rules live in one place.
        columnFilterModel: columnFilters,
        assignTaskAgent,
      };

      sessionStorage.setItem(
        TASK_MANAGE_TABLE_FILTERS_KEY,
        JSON.stringify(tableFiltersPayload)
      );
      localStorage.setItem(
        TASK_MANAGE_TABLE_FILTERS_KEY,
        JSON.stringify(tableFiltersPayload)
      );
      persistMapListFiltersPatch({ tableFilters: tableFiltersPayload });
      window.dispatchEvent(new Event("taskManageFiltersChanged"));
    } catch (error) {
      console.error("Error saving task table filters:", error);
    }
  }, [taskIdFilter, taskIdInputValue, createdByFilter, sourceFilter, priority, active, inputFilters, columnFilters, assignTaskAgent, hasHydratedTableFilters]);

  useEffect(() => {
    if (previousResetTokenRef.current === filterResetToken) return;
    previousResetTokenRef.current = filterResetToken;

    setTaskIdFilter("");
    setTaskIdInputValue("");
    setInputValue("");
    setAreaInputValue("");
    setMobileNo("");
    setMobileNoInputValue("");
    setCreatedByFilter("");
    setSourceFilter("Everything");
    setPriority("Everything");
    setActive("Everything");
    setFinishedStatus("Everything");
    setInputFilters({});
    setColumnFilters({});
    setAssignTaskAgent(null);
    setColWidths({});
    setColOrderOverride(null);
    localStorage.removeItem(TASK_MANAGE_TABLE_LAYOUT_KEY);
    persistTaskAreaCodeValue("");
    // The accountNo persistence effect only ever calls persistTaskSearchValue
    // with a non-empty value (see the effect below), so clearing accountNo
    // here never purged its storage -- on the next refresh the mount-time
    // hydration effect would read the stale cached value back in and silently
    // undo the reset. Clear it explicitly, same as persistTaskAreaCodeValue.
    persistTaskSearchValue("");
    sessionStorage.removeItem(TASK_MANAGE_TABLE_FILTERS_KEY);
    localStorage.removeItem(TASK_MANAGE_TABLE_FILTERS_KEY);
    persistMapListFiltersPatch({
      tableFilters: {
        taskIdFilter: "",
        taskIdInputValue: "",
        createdByFilter: "",
        sourceFilter: "Everything",
        priorityFilter: "Everything",
        taskStatusFilter: "Everything",
        inputFilters: {},
        columnFilterModel: {},
        assignTaskAgent: null,
      },
    });
    // Reset also clears the assignee and lands back on page 0. It is never
    // debounced -- clearing text filters applies immediately (see the
    // free-text debounce effect above).
    setSelectedUserId("");
    setCurrentPage(0);
    window.dispatchEvent(new Event("taskManageFiltersChanged"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterResetToken]);

  useEffect(() => {
    const persistTimer = window.setTimeout(() => {
      try {
        localStorage.setItem(
          TASK_MANAGE_TABLE_LAYOUT_KEY,
          JSON.stringify({
            colWidths,
            colOrder: colOrderOverride,
          })
        );
      } catch (error) {
        console.error("Error saving task table layout:", error);
      }
    }, 120);

    return () => window.clearTimeout(persistTimer);
  }, [colWidths, colOrderOverride]);

  /* ---------------------------------------------------------------------
     Free-text debounce.

     Only the free-text inputs are debounced. Filter OK / Reset / paging must
     stay instant, so they feed the request key directly and never route
     through here. Clearing everything also applies immediately -- a Reset
     shouldn't sit behind a 350ms timer waiting to show unfiltered results.
  --------------------------------------------------------------------- */
  const rawTextFilters = useMemo(
    () => ({
      taskIdFilter: String(taskIdFilter ?? "").trim(),
      accountNo: String(accountNo ?? "").trim(),
      areaCode: String(areaCode ?? "").trim(),
      createdByFilter: String(createdByFilter ?? "").trim(),
      mobileNo: String(MobileNo ?? "").trim(),
      inputFilters: inputFilters || {},
    }),
    [taskIdFilter, accountNo, areaCode, createdByFilter, MobileNo, inputFilters]
  );

  const [debouncedTextFilters, setDebouncedTextFilters] = useState(rawTextFilters);
  const rawTextFiltersKey = JSON.stringify(rawTextFilters);

  useEffect(() => {
    const parsed = JSON.parse(rawTextFiltersKey);
    const isCleared =
      !parsed.taskIdFilter &&
      !parsed.accountNo &&
      !parsed.areaCode &&
      !parsed.createdByFilter &&
      !parsed.mobileNo &&
      !Object.values(parsed.inputFilters || {}).some((value) => String(value ?? "").trim());

    if (isCleared) {
      setCurrentPage(0);
      setDebouncedTextFilters(parsed);
      return undefined;
    }
    const timer = setTimeout(() => {
      setCurrentPage(0);
      setDebouncedTextFilters(parsed);
    }, TEXT_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rawTextFiltersKey]);

  /* ---------------------------------------------------------------------
     The single filter model.

     Every filter on the screen -- toolbar text boxes, enum dropdowns, the
     created-date range and the per-column funnels -- collapses into one
     Record<columnKey, ColumnFilter> that gets serialised and sent whole on
     every fetch. The server holds no filter state, so anything missing from
     the payload is simply not applied.

     Funnel selections are merged last and therefore win over the toolbar
     control for the same column.
  --------------------------------------------------------------------- */
  /* Resolves a grid header key to the column name the search endpoint expects,
     by looking the header up in the /list/columns response and using that
     entry's own key. The metadata is the authority: the key it reports is
     exactly what `columnFilters[].column` has to say, whether that turns out to
     be "Account No.", "status" or "finishedDate".

     Matching is done on the canonical (case- and punctuation-insensitive) form,
     which is how the header and the metadata are already compared for
     visibility, so the two decisions can't disagree. Falls back to
     toColumnKey() only while the metadata is still loading. */
  const apiColumnKeyByCanonical = useMemo(() => {
    const byCanonical = new Map();
    (workflowColumns || []).forEach((col) => {
      const key = String(col?.key ?? "").trim();
      if (!key) return;
      const canonical = canonicalColumnKey(key);
      if (!byCanonical.has(canonical)) byCanonical.set(canonical, key);
    });
    return byCanonical;
  }, [workflowColumns]);

  const columnKeyForHeader = useCallback(
    (headerKey) => {
      const raw = String(headerKey ?? "").trim();
      if (!raw) return "";
      return apiColumnKeyByCanonical.get(canonicalColumnKey(raw)) || toColumnKey(raw);
    },
    [apiColumnKeyByCanonical]
  );

  const deniedColumnKeySet = useMemo(
    () =>
      new Set(
        (workflowColumns || [])
          .filter((col) => col?.accessible === false)
          .map((col) => String(col?.key ?? "").trim())
          .filter(Boolean)
      ),
    [workflowColumns]
  );

  /* Same denials, keyed canonically so a visibility check can consult them
     without knowing the exact spelling /list/columns used. */
  const deniedColumnCanonicalSet = useMemo(
    () => new Set([...deniedColumnKeySet].map((key) => canonicalColumnKey(key))),
    [deniedColumnKeySet]
  );

  const activeColumnFilters = useMemo(() => {
    const model = {};
    const put = (headerKey, filter) => {
      const colKey = columnKeyForHeader(headerKey);
      // Never filter on a column this user has been denied. Only explicit
      // denials count: /list/columns doesn't necessarily enumerate every fixed
      // column, so treating "absent" as "denied" would discard valid filters.
      if (deniedColumnKeySet.has(colKey) || deniedColumnCanonicalSet.has(canonicalColumnKey(colKey))) return;
      // Assigned To is carried by assigneeIds instead -- see the memo below.
      // Sending it here as well would filter the same thing twice, against a
      // column the search endpoint doesn't accept.
      if (isAssignedToColumnKey(colKey)) return;
      // Stamp the resolved key onto the entry so the map key and the `column`
      // that goes on the wire can never disagree -- the make*Filter helpers only
      // know the static fallback table, not the live metadata.
      const resolved = { ...filter, column: colKey };
      if (isMeaningfulColumnFilter(resolved)) model[colKey] = resolved;
    };

    put("taskId", makeContainsFilter("taskId", debouncedTextFilters.taskIdFilter));
    put("Account No.", makeContainsFilter("Account No.", debouncedTextFilters.accountNo));
    put("Area Code", makeContainsFilter("Area Code", debouncedTextFilters.areaCode));
    put("created_By", makeContainsFilter("created_By", debouncedTextFilters.createdByFilter));
    if (projectId === 19) {
      put("Mobile No", makeContainsFilter("Mobile No", debouncedTextFilters.mobileNo));
    }

    // Toolbar "Find a field..." picker -- a contains filter on the chosen
    // column, exactly like any other. There is no separate searchColumn /
    // searchValue parameter on this endpoint.
    Object.entries(debouncedTextFilters.inputFilters || {}).forEach(([column, value]) => {
      put(column, makeContainsFilter(column, value));
    });

    const normalizedPriority = normalizePriorityFilterValue(priority);
    if (normalizedPriority && normalizedPriority !== "Everything") {
      put("priority", makeInFilter("priority", [normalizedPriority]));
    }
    const normalizedStatus = normalizeTaskStatusFilterValue(active);
    if (normalizedStatus && normalizedStatus !== "Everything") {
      put("taskStatus", makeInFilter("taskStatus", [normalizedStatus]));
    }
    const normalizedSource = normalizeSourceFilterValue(sourceFilter);
    if (normalizedSource && normalizedSource !== "Everything") {
      put("source", makeInFilter("source", [normalizedSource]));
    }

    put("createdDate", makeBetweenFilter("createdDate", fromDate, toDate));

    Object.values(columnFilters || {}).forEach((filter) => put(filter?.column, filter));

    return model;
  }, [
    debouncedTextFilters,
    projectId,
    priority,
    active,
    sourceFilter,
    fromDate,
    toDate,
    columnFilters,
    deniedColumnKeySet,
    columnKeyForHeader,
  ]);

  const serializedColumnFilters = useMemo(
    () => serializeColumnFilters(activeColumnFilters),
    [activeColumnFilters]
  );

  // The fetch effect only runs when the request key changes, and this payload is
  // the bulk of that key. If typing in the search box produces no log line here,
  // the filter never reached the payload -- look for the "ignored" warning from
  // put() above, which names the column and why.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    console.info("[task-list] columnFilters payload ->", serializedColumnFilters);
  }, [serializedColumnFilters]);

  const activeColumnFilterCount = useMemo(
    () => countActiveColumnFilters(activeColumnFilters),
    [activeColumnFilters]
  );

  /* The Assigned To funnel stores assignee IDs. They leave the column-filter
     model here and become the request's `assigneeIds`, which is what both
     POST list/search and POST list/generate-report filter assignees on. */
  const funnelAssigneeIds = useMemo(() => {
    const entry = Object.values(columnFilters || {}).find((filter) =>
      isAssignedToColumnKey(filter?.column)
    );
    if (!entry || entry.op !== "in") return [];
    return (entry.values || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }, [columnFilters]);

  // Omitting assigneeIds entirely means "any assignee".
  const assigneeIds = useMemo(() => {
    // The funnel wins over the toolbar's single-agent dropdown for the same
    // column, exactly like every other funnel/toolbar pair on this screen.
    if (funnelAssigneeIds.length) return funnelAssigneeIds;

    const selected = assignTaskAgent !== null && assignTaskAgent !== undefined && assignTaskAgent !== ""
      ? assignTaskAgent
      : selectedUserId;
    const numeric = Number(selected);
    return Number.isFinite(numeric) && String(selected ?? "").trim() !== "" ? [numeric] : [];
  }, [funnelAssigneeIds, assignTaskAgent, selectedUserId]);

  const priorityoptions = PRIORITY_FILTER_OPTIONS;

  const activeoptions = taskStatusFlatOptions;

  const sourceOptions = [
    { value: "Everything", label: "All" },
    { value: "Automated", label: "Automated" },
    { value: "Manual", label: "Manual" },
    { value: "Imported", label: "Imported" },
    { value: "Mobile", label: "Mobile" },
  ];

  const resolvedPriorityValue = useMemo(() => {
    const normalizedPriority = normalizePriorityFilterValue(priority);
    const contextIsValid = priorityoptions.some((option) => option.value === normalizedPriority);
    return contextIsValid ? normalizedPriority : "Everything";
  }, [priority]);

  const resolvedStatusValue = useMemo(() => {
    const normalizedTaskStatus = normalizeTaskStatusFilterValue(active);
    const contextIsValid = activeoptions.some((option) => option.value === normalizedTaskStatus);
    return contextIsValid ? normalizedTaskStatus : "Everything";
  }, [active, activeoptions]);

  const resolvedSourceValue = useMemo(() => {
    const normalizedSource = normalizeSourceFilterValue(sourceFilter);
    return sourceOptions.some((option) => option.value === normalizedSource)
      ? normalizedSource
      : "Everything";
  }, [sourceFilter, sourceOptions]);

  const handleSourceFilterChange = (opt) => {
    const nextSource = normalizeSourceFilterValue(opt?.value);
    setSourceFilter(nextSource);
    persistTaskManageFiltersPatch({ sourceFilter: nextSource });
    persistMapListFiltersPatch({ selectedSource: nextSource });
  };

  const resolvedAssignedToValue = useMemo(() => {
    if (assignTaskAgent !== undefined && assignTaskAgent !== null) {
      return assignTaskAgent;
    }
    return selectedUserId ?? "";
  }, [assignTaskAgent, selectedUserId]);

  const previousPriority = useRef(priority);
  const previousPageSize = useRef(pageSize);
  // NOTE: stale-response detection lives in searchRequestIdRef, a ref local to
  // this component, and is deliberately never placed in an effect's dependency
  // array -- an effect that both mutates and watches the same counter re-runs
  // itself forever (this file used to have exactly that bug, showing up as
  // "many duplicate get-tasks calls"). The "please refetch" signal from
  // outside is `taskListRefreshKey`, a real state value bumped by
  // bumpTaskListFetchEpoch, which is safe to depend on.


  // Every filter now resolves in SQL over the whole workflow before paging, so
  // there is exactly one fetch feeding the table and one loading flag.
  const effectiveLoading = loading;

  // Whether any filter (server-side or client-only) is currently narrowing
  // the list, so the empty state can tell "nothing has ever been created"
  // apart from "your filters don't match anything" instead of always
  // showing the same generic message.
  const hasAnyFilterActive = useMemo(
    () =>
      Boolean(
        activeColumnFilterCount > 0 || assigneeIds.length > 0 || provideTasksWithMissingLatAndLong
      ),
    [activeColumnFilterCount, assigneeIds, provideTasksWithMissingLatAndLong]
  );

  /* ---------------------------------------------------------------------
     Export payload, published upward.

     The Excel export is "this query, unpaged", so it has to be built from the
     same serialised filters the fetch above sends -- not re-derived from the
     persisted filter blob, which is a display-oriented copy and applies none of
     the column-access denials. The toolbar owns the button but is a sibling of
     this component, so TaskManageMain holds the payload between them (same
     pattern as onTableHeadersChange / onSearchFieldsReady).

     filterCount counts what is actually in the payload -- serialised column
     filters after the drop rules and the 15-filter cap, plus the two non-column
     constraints -- so the button's "(N filters)" can never overstate how
     narrowed the export is.
  --------------------------------------------------------------------- */
  const exportRequest = useMemo(
    () => ({
      workflowIds: activityId,
      assigneeIds,
      provideTasksWithMissingLatAndLong,
      columnFilters: serializedColumnFilters,
      filterCount:
        serializedColumnFilters.length +
        (assigneeIds.length ? 1 : 0) +
        (provideTasksWithMissingLatAndLong ? 1 : 0),
    }),
    [activityId, assigneeIds, provideTasksWithMissingLatAndLong, serializedColumnFilters]
  );

  useEffect(() => {
    if (typeof onExportRequestChange === "function") {
      onExportRequestChange(exportRequest);
    }
  }, [exportRequest, onExportRequestChange]);

  /* ---------------------------------------------------------------------
     The one and only list fetch.

     Filtering, paging and totals are all server-side, so this is the single
     source of the rows on screen. It runs on mount, page change, size change,
     filter OK, filter clear, Reset, workflow change and assignee change -- but
     never on an individual checkbox tick, because those only mutate the
     picker's local draft until OK is pressed.
  --------------------------------------------------------------------- */
  const searchRequestKey = useMemo(
    () =>
      JSON.stringify({
        activityId,
        currentPage,
        pageSize,
        assigneeIds,
        provideTasksWithMissingLatAndLong,
        serializedColumnFilters,
      }),
    [
      activityId,
      currentPage,
      pageSize,
      assigneeIds,
      provideTasksWithMissingLatAndLong,
      serializedColumnFilters,
    ]
  );

  useEffect(() => {
    if (!filtersHydrated) return undefined;
    if (!activityId) {
      setTaskData([]);
      setTotalPageSize(0);
      setLoading(false);
      return undefined;
    }

    const abortController = new AbortController();
    const requestId = ++searchRequestIdRef.current;

    // Switching to another activity drops the old activity's rows right away; doing it here
    // (not in a separate effect) keeps it ordered before this request's response.
    if (lastFetchedActivityRef.current !== activityId) {
      lastFetchedActivityRef.current = activityId;
      setTaskData([]);
      setTotalPageSize(0);
    }

    setLoading(true);
    setSearchErrorCode(null);

    (async () => {
      try {
        const result = await searchTasks({
          workflowIds: activityId,
          page: currentPage,
          size: Math.min(pageSize, MAX_PAGE_SIZE),
          assigneeIds,
          provideTasksWithMissingLatAndLong,
          columnFilters: serializedColumnFilters,
          signal: abortController.signal,
        });

        // Discard anything that is no longer the newest request. Aborting
        // isn't sufficient on its own: a response can already be past the
        // point where the signal has any effect.
        if (requestId !== searchRequestIdRef.current) return;

        const fetchedList = result.taskList || [];
        const fetchedTotal = result.totalSize || 0;

        // Deleting the last row(s) on a page beyond the first can leave
        // currentPage pointing past the new last page. Step back to a page
        // that actually has rows instead of rendering a blank table.
        const newLastPageIndex = Math.max(Math.ceil(fetchedTotal / pageSize) - 1, 0);
        if (fetchedList.length === 0 && currentPage > 0 && currentPage > newLastPageIndex) {
          setLoading(false);
          setCurrentPage(newLastPageIndex);
          return;
        }

        // Checkbox state lives on the rows (task.isChecked), which a refetch
        // replaces with unchecked ones. Keep the selection in step with what
        // is actually on screen: re-tick rows that were selected and still
        // exist, and drop selected ids that are no longer in the list.
        // Otherwise those hidden ids stay in the selection with stale statuses.
        const keptIds = new Set((selectedTaskIdsRef.current || []).map((id) => String(id)));
        const reconciledList = fetchedList.map((task) =>
          keptIds.has(String(task.taskId)) && isTaskSelectable(task) ? { ...task, isChecked: true } : task
        );
        const reconciledSelected = reconciledList.filter((task) => task.isChecked);
        const selectableRows = reconciledList.filter(isTaskSelectable);
        setTaskData(reconciledList);
        setselectedtask(reconciledSelected.map((task) => task.taskId));
        setSelectedTasks(reconciledSelected);
        setIsChecked(reconciledSelected.length > 0);
        setSelectAll(selectableRows.length > 0 && selectableRows.every((task) => task.isChecked));
        // totalSize is the filtered total across the whole workflow. Never
        // derive it from the rows in hand -- there are only `size` of them.
        setTotalPageSize(fetchedTotal);
        setLoading(false);
      } catch (error) {
        if (
          axios.isCancel(error) ||
          error?.name === "CanceledError" ||
          error?.name === "AbortError"
        ) {
          return;
        }
        if (requestId !== searchRequestIdRef.current) return;

        // These all arrive as HTTP 200 with the real status in header.code,
        // so axios never treats them as failures on its own.
        if (error instanceof TaskListSearchError) {
          setSearchErrorCode(error.code);
          if (error.code === TASK_LIST_CODES.ACCESS_DENIED) {
            toast.error(
              translations[langMode]?.accessDenied ?? "You do not have access to these tasks."
            );
          } else if (error.code === TASK_LIST_CODES.TOKEN_EXPIRED) {
            // App.js owns session teardown and the redirect; just stop
            // showing rows the user is no longer entitled to.
            toast.error(
              translations[langMode]?.sessionExpired ??
                "Your session has expired. Please sign in again."
            );
          } else {
            // 601 validation and anything unmapped: surface body.value.
            toast.error(error.message);
          }
        } else {
          console.error("Error searching tasks:", error);
        }
        setTaskData([]);
        setTotalPageSize(0);
        setLoading(false);
      }
    })();

    return () => abortController.abort();
    // searchRequestKey collapses every real input into one signature, so the
    // individual values it is built from are deliberately not repeated here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchRequestKey, filtersHydrated, taskListRefreshKey]);

  useEffect(() => {
    fetchFieldAgentMap(activityId)
      .then((res) => {
        console.log('resresresresres', res.body);

        const agentOptions = res?.body.value?.map((agent) => ({
          value: agent.userId,
          label: agent.name || agent.email || "Unnamed Agent", // adjust based on available fields
        }));
        setOptionsTaskAgent(agentOptions);
      })
      .catch((err) => {
        console.error("Failed to fetch agents:", err);
      })
      .finally(() => {
      });
  }, [activityId]);

  // Restrict the field-agent filter to members of the groups the current admin/office user
  // has access to. Super admins (and users with no assigned groups) are left unrestricted.
  useEffect(() => {
    let cancelled = false;

    const userType = getCurrentUserType();
    const userGroupIds = getCurrentUserGroupIds();
    if (userType === "super_admin" || userGroupIds.length === 0) {
      setGroupAllowedAgentIds(null);
      return undefined;
    }

    (async () => {
      try {
        const groups = await loadGroupsForDepartment(); // already filtered to the user's accessible groups
        const memberIds = getGroupMemberIdSet(groups);
        if (!cancelled) setGroupAllowedAgentIds(memberIds);
      } catch (err) {
        console.error("Failed to load group-based field agent access:", err);
        if (!cancelled) setGroupAllowedAgentIds(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activityId]);

  // Field-agent options visible in the "Assigned To" filter after applying group-based access.
  const visibleAgentOptions = useMemo(() => {
    const list = optionsTaskAgent || [];
    if (!groupAllowedAgentIds) return list;
    return list.filter((option) => groupAllowedAgentIds.has(String(option?.value)));
  }, [optionsTaskAgent, groupAllowedAgentIds]);

  const totalPages = Math.ceil(totalPageSize / pageSize) - 1;
  const totalItems = totalPageSize;
  const startIndex = ((currentPage + 1) - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const selectedTaskIdsRef = useRef(selectedtask);
  selectedTaskIdsRef.current = selectedtask;

  const handlePageSizeChange = (value) => {
    const size = parseInt(value, 10);
    setPageSize(size);
    setCurrentPage(0);
    // Changing page size fetches a whole new set of rows, so any selection
    // made against the old page size (e.g. "10 tasks selected" from the
    // default page) no longer corresponds to what's on screen -- clear it
    // along with the checkbox state instead of leaving a stale count behind.
    setSelectAll(false);
    setselectedtask([]);
    setSelectedTasks([]);
    setIsChecked(false);
  };

  // Navigating to a different page fetches fresh rows with no `isChecked`
  // flag, so the old selection (and the "select all" header checkbox) would
  // otherwise be left stale -- clear it so the checkbox state on screen
  // always matches the page actually shown.
  useEffect(() => {
    setSelectAll(false);
    setselectedtask([]);
    setSelectedTasks([]);
    setIsChecked(false);
  }, [currentPage]);

  // Only resets the page. It must not clear the rows: workflowId / selectedOrgId are filled in by
  // the nav selectors a moment after mount, usually after the list search (which starts straight
  // away from the stored activityId) has already answered. Clearing here then wiped rows that
  // nothing would refetch, leaving the total on screen with an empty table.
  useEffect(() => {
    if ((selectedOrgId, workflowId)) {
      setCurrentPage(0);
    }

  }, [selectedOrgId, workflowId, activityId]);

  useEffect(() => {
    if (projectId === null) {
      localStorage.removeItem("activityId")
    }
  }, [projectId])


  const handleSearch = () => {
    setAccountNo(inputValue); // Set the final value on search
    persistTaskSearchValue(inputValue);
  };

  useEffect(() => {
    const savedSearch =
      sessionStorage.getItem(TASK_SEARCH_VALUE_KEY) ||
      sessionStorage.getItem(TASK_SEARCH_VALUE_BACKUP_KEY) ||
      localStorage.getItem(TASK_SEARCH_VALUE_BACKUP_KEY) ||
      "";

    if (savedSearch) {
      setAccountNo(savedSearch);
      setInputValue(savedSearch);
      persistTaskSearchValue(savedSearch);
    }
    searchHydratedRef.current = true;
  }, []);

  useEffect(() => {
    const savedAreaCode =
      sessionStorage.getItem(TASK_AREA_CODE_VALUE_KEY) ||
      sessionStorage.getItem(TASK_AREA_CODE_VALUE_BACKUP_KEY) ||
      localStorage.getItem(TASK_AREA_CODE_VALUE_BACKUP_KEY) ||
      "";

    if (savedAreaCode) {
      setAreaCode(savedAreaCode);
      setAreaInputValue(savedAreaCode);
      persistTaskAreaCodeValue(savedAreaCode);
    }
    areaHydratedRef.current = true;
  }, [setAreaCode]);

  useEffect(() => {
    if (!searchHydratedRef.current) return;
    if (String(accountNo ?? "").trim()) {
      persistTaskSearchValue(accountNo);
    }
  }, [accountNo]);

  useEffect(() => {
    if (!areaHydratedRef.current) return;
    if (String(areaCode ?? "").trim()) {
      persistTaskAreaCodeValue(areaCode);
    }
  }, [areaCode]);

  useEffect(() => {
    const storedPriority = Cookies.get("mapListFilters");
    if (storedPriority) {
      const parsedPriority = JSON.parse(storedPriority);
      const savedAreaCode =
        sessionStorage.getItem(TASK_AREA_CODE_VALUE_KEY) ||
        sessionStorage.getItem(TASK_AREA_CODE_VALUE_BACKUP_KEY) ||
        localStorage.getItem(TASK_AREA_CODE_VALUE_BACKUP_KEY) ||
        parsedPriority?.areaInput ||
        "";
      setAreaInputValue(savedAreaCode);
      if (savedAreaCode) {
        setAreaCode(savedAreaCode);
      }
      setMobileNoInputValue(parsedPriority?.mobile ?? "");
      setMobileNo(parsedPriority?.mobile ?? "");
      if (parsedPriority?.selectedSource) {
        setSourceFilter(normalizeSourceFilterValue(parsedPriority.selectedSource));
      }
      if (Array.isArray(fieldAgentList)) {
        const selectedAgent = (fieldAgentList || [])?.find(
          (agent) => agent.userId === parsedPriority?.selectedUserId
        );
        if (selectedAgent) {
          setSelectedUserName(selectedAgent.name);
        }
      }
    }

  }, [priorityoptions, theme, fieldAgentList]);

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < effectiveLastPageIndex) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Finished tasks are done and can't be assigned/reassigned/deleted in bulk,
  // so their row checkbox is hidden -- keep selection logic consistent with
  // that by never letting a Finished task join the selection.
  const isTaskSelectable = (task) => task?.taskStatus !== "Finished";

  const handleCheckboxChange = (index) => {
    const newData = [...taskData];
    const taskObject = newData[index];
    if (!isTaskSelectable(taskObject)) return;
    newData[index].isChecked = !taskObject.isChecked;

    if (newData.some((task) => task.isChecked)) {
      setIsChecked(true);
    } else {
      setIsChecked(false);
    }

    if (newData[index].isChecked) {
      setSelectedTasks([...selectedTasks, taskObject]);
      setselectedtask([...selectedtask, taskObject.taskId]);
    } else {
      setSelectedTasks((prevTasks) =>
        prevTasks.filter((task) => task.taskId !== taskObject.taskId)
      );
      setselectedtask((prevValues) =>
        prevValues.filter((value) => value !== taskObject.taskId)
      );
    }

    // Keep the header checkbox in sync when every selectable row on the
    // current page ends up checked (or unchecked) one at a time, not just
    // via "select all". Finished tasks have no checkbox, so they're ignored.
    const selectableRows = newData.filter(isTaskSelectable);
    setSelectAll(selectableRows.length > 0 && selectableRows.every((task) => task.isChecked));

    setTaskData(newData || []);
  };

  const handleSelectAllChange = () => {
    const newData = taskData?.map((task) => ({
      ...task,
      isChecked: isTaskSelectable(task) ? !selectAll : false,
    }));

    if (!selectAll) {
      const selectedRows = newData.filter((task) => task.isChecked);
      setSelectedTasks(selectedRows);
      const allTaskIds = selectedRows.map((task) => task.taskId);
      setselectedtask(allTaskIds);
      setIsChecked(selectedRows.length > 0);
      setAssigneesToUnassign(selectedRows);
    } else {
      setSelectedTasks([]);
      setselectedtask([]);
      setIsChecked(false);
    }

    setTaskData(newData || []);
    setSelectAll(!selectAll);
  };

  const updateTaskPriorityHandler = async (taskId, newPriority) => {
    if (!canUpdateTask) return;
    try {
      const responseData = await updateTaskPriority(taskId, newPriority);
      if (responseData.header.code === 600) {
        if (typeof bumpTaskListFetchEpoch === "function") {
          bumpTaskListFetchEpoch();
        }
        const updatedTaskData = taskData.map((task) => {
          if (task.taskId === taskId) {
            return {
              ...task,
              priority: newPriority,
            };
          }
          return task;
        });
        setTaskData(updatedTaskData || []);
        setselectedtask(
          updatedTaskData
            .filter((task) => task.isChecked)
            .map((task) => task.taskId)
        );
        toast.success(translations[langMode].taskprioriyupdated);
      } else {
        console.error("Failed to update priority:", responseData.statusText);
      }
    } catch (error) {
      console.error("Error updating task priority:", error);
    }
  };

  const handleTaskClick = (taskId, accountNo) => {
    setSelectedTaskForSlider({ taskId, accountNo });
  };

  const baseHeaders = useMemo(() => [
    { value: "taskId", label: translations[langMode].taskId },
    { value: "Account No.", label: translations[langMode].accno },
    { value: "Activity", label: translations[langMode].Activity },
    { value: "priority", label: translations[langMode].priority },
    { value: "cycle", label: translations[langMode].Cycle },
    // keep "Completed date" as the 6th base header (1-based)
    { value: "completedDate", label: translations[langMode].comapletedDate },
    { value: "taskStatus", label: translations[langMode].taskstatus },
    { value: "source", label: translations[langMode].source },
    { value: "Area Code", label: translations[langMode].areCode },
    { value: "created_By", label: translations[langMode].created_By },
    { value: "createdDate", label: translations[langMode].createdatetime },
    { value: "assignedTo", label: translations[langMode].assignedTo ?? "Assigned To" },
    { value: "taskCategory", label: translations[langMode].taskCategory },
    { value: "Comment", label: translations[langMode].comment },
    { value: TASK_LOCATION_KEY, label: translations[langMode]?.taskLocationColumn ?? "Location" },
    ...(projectId === 19 ? [{ value: "Mobile No", label: translations[langMode].mobileno }] : []),
  ], [langMode, projectId]);

  const BASE_DATA_COLUMN_MIN_WIDTH_PX = 110;
  const TIGHT_FIT_DATA_COLUMN_MIN_WIDTH_PX = 100;
  const TIGHT_FIT_VISIBLE_COLUMNS_MIN = 4;
  const TIGHT_FIT_VISIBLE_COLUMNS_MAX = 5;
  const TIGHT_FIT_VIEWPORT_BUFFER_PX = 72;

  const isTightFitLayout = () => {
    const visibleDataColumnCount = (visibleTableHeaders || []).length;
    const baselineLeadingColumnsWidthPx = 60 + 60 + (showTaskActionColumn ? 108 : 0);
    const baselineTableMinWidthPx =
      baselineLeadingColumnsWidthPx + (visibleDataColumnCount * BASE_DATA_COLUMN_MIN_WIDTH_PX);
    return (
      visibleDataColumnCount >= TIGHT_FIT_VISIBLE_COLUMNS_MIN &&
      visibleDataColumnCount <= TIGHT_FIT_VISIBLE_COLUMNS_MAX &&
      tableViewportWidth > 0 &&
      tableViewportWidth < (baselineTableMinWidthPx + TIGHT_FIT_VIEWPORT_BUFFER_PX)
    );
  };

  const getDataCellPaddingXClass = () => "px-2";

  const getTableColumnWidthClass = () => "";

  const getTableColumnMinWidthPx = (header) => {
    const key = String(header?.value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const compactWidths = {
      taskid: 125,
      accountno: 125,
      activity: 190,
      priority: 112,
      cycle:130,
      assignedto: 180,
      assigneenames: 180,
      taskstatus: 140,
      completeddate: 165,
      source: 125,
      areacode: 110,
      createdby: 150,
      createddate: 160,
      taskcategory: 140,
      comment: 180,
      mobileno: 130,
      tasklocation: 100,
      uploadeddate: 130,
      uploadedtime: 115,
    };
    if (compactWidths[key]) return compactWidths[key];

    const labelLength = String(header?.label || header?.value || "").trim().length;
    const estimatedWidth = (labelLength * 7) + 38;
    const minimum = isTightFitLayout()
      ? TIGHT_FIT_DATA_COLUMN_MIN_WIDTH_PX
      : BASE_DATA_COLUMN_MIN_WIDTH_PX;
    return Math.max(minimum, Math.min(200, estimatedWidth));
  };

  const getFilterActivationValue = useCallback((fieldKey, value) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) return "";

    const neutralByField = {
      priority: new Set(["Everything", "Priority"]),
      taskStatus: new Set(["Everything", "Status"]),
      source: new Set(["Everything", "Source"]),
      assignedTo: new Set(["Everything", "All"]),
    };

    if (neutralByField[fieldKey]?.has(normalized)) return "";
    return normalized;
  }, []);
  const getTableCellClass = () => {
    const base = `${getDataCellPaddingXClass()} py-2.5 text-xs font-normal text-left`;
    return `${base} truncate overflow-hidden whitespace-nowrap`;
  };

  /* Shared with the filter model (see columnKeyForHeader above), so a header and
     a /list/columns entry are judged "the same column" identically for both
     visibility and filtering. The finishedDate/completedDate alias lives in
     canonicalColumnKey: without it the accessible set holds one spelling while
     the lookup asks for the other, so the column was treated as "not granted"
     and silently dropped -- and an explicit denial never applied either. */
  const getCanonicalHeaderKey = useCallback((headerKey = "") => canonicalColumnKey(headerKey), []);

  const accessibleColumns = useMemo(
    () => (workflowColumns || []).filter((col) => col?.accessible !== false),
    [workflowColumns]
  );
  const isRestrictedColumnAccessUser = useMemo(() => {
    const currentUserType = getCurrentUserType?.();
    return ["admin", "supervisor", "office_user"].includes(String(currentUserType || "").toLowerCase());
  }, []);

  const accessibleColumnCanonicalSet = useMemo(
    () => new Set(accessibleColumns.map((col) => getCanonicalHeaderKey(col?.key))),
    [accessibleColumns, getCanonicalHeaderKey]
  );

  const isSuperAdminUser = useMemo(
    () => String(getCurrentUserType?.() || "").toLowerCase() === "super_admin",
    []
  );

  /* Assignee Names is a fixed system column: /list/columns only enumerates the
     grantable task_details_json fields, so it is frequently absent from the
     response even though every user is entitled to it. Treating "absent" as
     "denied" is what hid Assigned To from the grid. A super admin sees every
     column by definition, so keep it visible for them regardless of what the
     access list says -- other user types still go through the normal check. */
  const SUPER_ADMIN_ALWAYS_VISIBLE_CANONICAL_KEYS = useMemo(
    () => new Set(["assignedto", "assigneenames"]),
    []
  );

  /* Fixed system columns: top-level task properties that POST list/search
     returns for every task, as opposed to the grantable task_details_json
     fields /list/columns enumerates. Being absent from that response is the
     normal case for them, not a denial -- which is what hid Cycle from the
     grid even though every task carried a `cycle` value.

     So these follow the same "only explicit denials count" rule that
     activeColumnFilters already applies (see deniedColumnKeySet above), and
     stay visible to every user type unless /list/columns names them with
     accessible: false. That is deliberately broader than the super-admin
     escape hatch above: cycle is not role-scoped, so gating it on super admin
     would leave admin/supervisor/office_user unable to see it at all. */
  const SYSTEM_COLUMN_CANONICAL_KEYS = useMemo(
    () =>
      new Set([
        "accountno",
        "taskid",
        "areacode",
        "createdby",
        "createddate",
        "priority",
        "taskstatus",
        "source",
        "completeddate",
        "assignedto",
        "assigneenames",
        "activity",
        "taskcategory",
        "comment",
        "cycle",
        canonicalColumnKey(TASK_LOCATION_KEY),
        "mobileno",
      ]),
    []
  );

  const isAccessibleColumn = useCallback(
    (headerKey) => {
      if (!workflowColumnsLoaded) return true;
      const canonicalKey = getCanonicalHeaderKey(headerKey);
      if (SYSTEM_COLUMN_CANONICAL_KEYS.has(canonicalKey)) {
        return !deniedColumnCanonicalSet.has(canonicalKey);
      }
      if (isSuperAdminUser && SUPER_ADMIN_ALWAYS_VISIBLE_CANONICAL_KEYS.has(canonicalKey)) {
        return true;
      }
      return accessibleColumnCanonicalSet.has(canonicalKey);
    },
    [
      workflowColumnsLoaded,
      accessibleColumnCanonicalSet,
      getCanonicalHeaderKey,
      isSuperAdminUser,
      SUPER_ADMIN_ALWAYS_VISIBLE_CANONICAL_KEYS,
      SYSTEM_COLUMN_CANONICAL_KEYS,
      deniedColumnCanonicalSet,
    ]
  );

  const filteredBaseHeaders = useMemo(
    () => baseHeaders.filter((header) => isAccessibleColumn(header.value)),
    [baseHeaders, isAccessibleColumn]
  );

  const noColumnAccess = useMemo(() => {
    if (!workflowColumnsLoaded || !activityId || !isRestrictedColumnAccessUser) return false;
    return filteredBaseHeaders.length === 0 && accessibleColumns.length === 0;
  }, [workflowColumnsLoaded, activityId, isRestrictedColumnAccessUser, filteredBaseHeaders, accessibleColumns]);

  useEffect(() => {
    if (typeof onColumnAccessStateChange === "function") {
      onColumnAccessStateChange(noColumnAccess);
    }
  }, [noColumnAccess, onColumnAccessStateChange]);

  const baseHeaderKeys = useMemo(
    () => filteredBaseHeaders.map((header) => getCanonicalHeaderKey(header.value.trim())),
    [filteredBaseHeaders, getCanonicalHeaderKey]
  );

  const getSafeTaskDetails = (taskDetails) => {
    try {
      if (typeof taskDetails === "object" && taskDetails !== null) {
        return taskDetails;
      }
      if (typeof taskDetails === "string") {
        return JSON.parse(taskDetails);
      }
    } catch (error) {
      console.warn("Invalid taskDetails format:", taskDetails);
    }
    return {};
  };

  const getTaskDetailValueByHeader = useCallback((taskDetails, headerKey) => {
    const details = getSafeTaskDetails(taskDetails);
    if (!details || typeof details !== "object") return "";

    if (Object.prototype.hasOwnProperty.call(details, headerKey)) {
      return details[headerKey];
    }

    const targetCanonical = getCanonicalHeaderKey(headerKey);
    const matchedKey = Object.keys(details).find(
      (key) => getCanonicalHeaderKey(key) === targetCanonical
    );

    if (matchedKey) return details[matchedKey];

    // Fuzzy fallback for minor key-shape drifts from backend (extra tokens, punctuation, etc.)
    const fuzzyMatchedKey = Object.keys(details).find((key) => {
      const canonical = getCanonicalHeaderKey(key);
      return canonical.includes(targetCanonical) || targetCanonical.includes(canonical);
    });

    return fuzzyMatchedKey ? details[fuzzyMatchedKey] : "";
  }, [getCanonicalHeaderKey]);

  const valueToFilterableText = useCallback((value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) {
      return value.map((item) => valueToFilterableText(item)).join(" ");
    }
    if (typeof value === "object") {
      const prioritizedKeys = ["value", "label", "name", "text", "displayName"];
      const prioritizedValues = prioritizedKeys
        .filter((key) => Object.prototype.hasOwnProperty.call(value, key))
        .map((key) => valueToFilterableText(value[key]))
        .filter(Boolean);
      if (prioritizedValues.length > 0) return prioritizedValues.join(" ");
      return Object.values(value)
        .map((item) => valueToFilterableText(item))
        .join(" ");
    }
    return String(value);
  }, []);

  const getTaskValueByHeader = useCallback((task, headerKey) => {
    const canonicalHeader = getCanonicalHeaderKey(headerKey);
    const topLevelByCanonical = {
      taskid: task?.taskId,
      accountno: task?.accountNo,
      createdby: task?.createdByName,
      createdbyname: task?.createdByName,
      latitude: task?.latitude,
      longitude: task?.longitude,
      meterno: task?.meterNo ?? task?.meterNO ?? task?.meter_No,
      category: Array.isArray(task?.taskCategory) ? task.taskCategory.join(" ") : task?.taskCategory,
      remarks: task?.remarks ?? task?.comment,
      comment: task?.comment,
      area: task?.area,
      tariff: task?.tariff,
      customertype: task?.customerType,
      customerclass: task?.customerClass,
      consumernameeng: task?.consumerNameEng,
      taskstatus: task?.taskStatus,
      source: task?.source,
      areacode: task?.areaCode,
      mobileno: task?.mobileNo,
      priority: task?.priority,
      cycle: task?.cycle,
      createddate: task?.createdDate,
      completeddate: task?.finishedDate,
      assignedto: (task?.assignees || []).map((a) => a?.assigneeName ?? "").filter(Boolean).join(", "),
    };

    const topLevelValue = topLevelByCanonical[canonicalHeader];
    if (topLevelValue !== undefined && topLevelValue !== null && String(topLevelValue).trim() !== "") {
      return topLevelValue;
    }

    return getTaskDetailValueByHeader(task?.taskDetails, headerKey);
  }, [getCanonicalHeaderKey, getTaskDetailValueByHeader]);

  const getInputFilterValueByHeader = useCallback((headerKey) => {
    const directValue = inputFilters?.[headerKey];
    if (directValue !== undefined) return directValue;

    const targetCanonical = getCanonicalHeaderKey(headerKey);
    const matchedKey = Object.keys(inputFilters || {}).find(
      (key) => getCanonicalHeaderKey(key) === targetCanonical
    );
    return matchedKey ? inputFilters[matchedKey] : "";
  }, [inputFilters, getCanonicalHeaderKey]);

  const dynamicHeaders = useMemo(() => {
    if (taskData && taskData.length > 0) {
      const cachedHeaders = dynamicHeadersCacheRef.current || [];
      const dynamicByCanonical = new Map(
        cachedHeaders
          .filter((header) => isAccessibleColumn(header?.value))
          .map((header) => [getCanonicalHeaderKey(header.value), header])
      );

      (taskData || []).forEach((task) => {
        Object.keys(getSafeTaskDetails(task?.taskDetails)).forEach((key) => {
          const canonicalKey = getCanonicalHeaderKey(key);
          if (!isAccessibleColumn(key)) return;
          if (baseHeaderKeys.includes(canonicalKey)) return;
          if (dynamicByCanonical.has(canonicalKey)) return;
          dynamicByCanonical.set(canonicalKey, {
            value: key,
            label: key,
          });
        });
      });

      const computedHeaders = Array.from(dynamicByCanonical.values());
      dynamicHeadersCacheRef.current = computedHeaders;
      return computedHeaders;
    }
    return dynamicHeadersCacheRef.current || [];
  }, [taskData, baseHeaderKeys, getCanonicalHeaderKey, isAccessibleColumn]);

  // The Location column is listed straight after the Latitude / Longitude columns when the
  // workflow has them, otherwise it keeps its place in the base headers.
  const tableHeaders = useMemo(() => {
    const all = [...filteredBaseHeaders, ...dynamicHeaders];
    const locationIndex = all.findIndex((header) => header.value === TASK_LOCATION_KEY);
    if (locationIndex === -1) return all;
    const withoutLocation = all.filter((header) => header.value !== TASK_LOCATION_KEY);
    let lastCoordinateIndex = -1;
    withoutLocation.forEach((header, index) => {
      const canonical = getCanonicalHeaderKey(header.value);
      if (canonical === "latitude" || canonical === "longitude") lastCoordinateIndex = index;
    });
    if (lastCoordinateIndex === -1) return all;
    withoutLocation.splice(lastCoordinateIndex + 1, 0, all[locationIndex]);
    return withoutLocation;
  }, [filteredBaseHeaders, dynamicHeaders, getCanonicalHeaderKey]);
  const visibleTableHeaders = useMemo(() => {
    if (!visibleColumnKeys?.length) return tableHeaders;

    const byKey = new Map((tableHeaders || []).map((header) => [header.value, header]));
    const orderedVisible = visibleColumnKeys
      .map((key) => byKey.get(key))
      .filter(Boolean);

    // Safety fallback: if persistence/order data is stale, keep table usable.
    return orderedVisible.length ? orderedVisible : tableHeaders;
  }, [tableHeaders, visibleColumnKeys]);

  // Drag-to-reorder is a persisted visual override on top of visibleTableHeaders.
  const orderedVisibleTableHeaders = useMemo(() => {
    if (!colOrderOverride) return visibleTableHeaders;
    const byKey = new Map(visibleTableHeaders.map((h) => [h.value, h]));
    const ordered = colOrderOverride.map((k) => byKey.get(k)).filter(Boolean);
    const missing = visibleTableHeaders.filter((h) => !colOrderOverride.includes(h.value));
    return [...ordered, ...missing];
  }, [visibleTableHeaders, colOrderOverride]);

  const startColumnResize = (e, key, startWidthPx) => {
    e.preventDefault();
    e.stopPropagation();
    resizeColRef.current = { key, startX: e.clientX, startWidth: startWidthPx };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const onMove = (e) => {
      const r = resizeColRef.current;
      if (!r) return;
      const next = Math.max(70, r.startWidth + (e.clientX - r.startX));
      setColWidths((w) => (w[r.key] === next ? w : { ...w, [r.key]: next }));
    };
    const onUp = () => {
      if (resizeColRef.current) {
        resizeColRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const handleColumnDragStart = (e, key) => {
    setDragColKey(key);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  };
  const handleColumnDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (key !== dragOverColKey) setDragOverColKey(key);
  };
  const handleColumnDrop = (e, key) => {
    e.preventDefault();
    setDragOverColKey(null);
    const draggedKey = dragColKey || e.dataTransfer.getData("text/plain");
    if (!draggedKey || draggedKey === key) { setDragColKey(null); return; }
    const baseOrder = (colOrderOverride || orderedVisibleTableHeaders.map((h) => h.value));
    const next = baseOrder.filter((k) => k !== draggedKey);
    const targetIndex = next.indexOf(key);
    if (targetIndex < 0) { setDragColKey(null); return; }

    const targetRect = e.currentTarget.getBoundingClientRect();
    const pointerIsAfterMidpoint = dir === "rtl"
      ? e.clientX < targetRect.left + targetRect.width / 2
      : e.clientX > targetRect.left + targetRect.width / 2;
    next.splice(targetIndex + (pointerIsAfterMidpoint ? 1 : 0), 0, draggedKey);
    setColOrderOverride(next);
    setDragColKey(null);
  };

  const handleTableDragOver = (e) => {
    if (!dragColKey) return;
    e.preventDefault();

    const viewport = tableViewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    const edgeSize = Math.min(72, rect.width * 0.2);
    const distanceFromLeft = e.clientX - rect.left;
    const distanceFromRight = rect.right - e.clientX;

    if (distanceFromLeft >= 0 && distanceFromLeft < edgeSize) {
      const speed = Math.max(8, Math.ceil((edgeSize - distanceFromLeft) / 3));
      viewport.scrollBy({ left: -speed, behavior: "auto" });
    } else if (distanceFromRight >= 0 && distanceFromRight < edgeSize) {
      const speed = Math.max(8, Math.ceil((edgeSize - distanceFromRight) / 3));
      viewport.scrollBy({ left: speed, behavior: "auto" });
    }
  };
  const handleColumnDragEnd = () => {
    setDragColKey(null);
    setDragOverColKey(null);
  };

  useEffect(() => {
    if (typeof onTableHeadersChange === "function") {
      onTableHeadersChange(tableHeaders);
    }
  }, [tableHeaders, onTableHeadersChange]);

  /* Any change to filters / assigneeIds / workflowIds / size resets to page 0
     before the next fetch. Without this you stay on page 3 of a result that
     now has a single page and see an empty grid that reads like a backend
     bug. Paging itself is deliberately excluded. */
  const pageResetKey = useMemo(
    () =>
      JSON.stringify({
        activityId,
        pageSize,
        assigneeIds,
        provideTasksWithMissingLatAndLong,
        serializedColumnFilters,
      }),
    [activityId, pageSize, assigneeIds, provideTasksWithMissingLatAndLong, serializedColumnFilters]
  );

  const previousPageResetKeyRef = useRef(pageResetKey);
  useEffect(() => {
    if (previousPageResetKeyRef.current === pageResetKey) return;
    previousPageResetKeyRef.current = pageResetKey;
    setCurrentPage(0);
  }, [pageResetKey]);

  /* The rows the server returned for this page, in the order it returned them.
     No filtering, sorting or slicing happens here: the grid only holds `size`
     rows out of the whole workflow, so a local predicate would narrow the page
     rather than the result set and would contradict totalSize.

     Group-based access is the one exception, because it is a visibility rule
     rather than a filter -- restricted users see only tasks assigned to their
     group members, plus unassigned Active tasks.

     NOTE: the AI/keyword box (aiSourceFilter) no longer narrows rows here. Its
     keyword-to-status/priority mapping still applies through the normal filter
     model, but free text has no server-side equivalent -- matching it against
     the loaded page would search 10 rows out of ~400k. It needs either a
     backend full-text op or to be scoped to a single column. */
  const displayTaskData = useMemo(() => {
    const list = taskData || [];
    if (!groupAllowedAgentIds) return list;
    return list.filter((task) => isTaskVisibleForGroupAccess(task, groupAllowedAgentIds));
  }, [taskData, groupAllowedAgentIds]);

  /* Group filtering runs first, above. The client-side pagination stage that
     used to follow it (isClientPaginated / narrowedTaskData, which re-sliced a
     client-narrowed dataset) has no inputs left here: the column/AI filter
     model is now sent to the backend (see serializedColumnFilters in the fetch
     payload), so the group rule is the only client-side narrowing that
     remains, and `taskData` is already one server page. Re-slicing it would
     render fewer than `pageSize` rows per page, so paging stays server-driven.

     The count is not derived here: it is the backend's totalSize,
     which already reflects the group rule for the logged-in user. */

  useEffect(() => {
    if (!selectedtask || selectedtask.length === 0) {
      if (taskData?.some((t) => t.isChecked)) {
        setTaskData((prev) => (prev || []).map((t) => t.isChecked ? { ...t, isChecked: false } : t));
      }
    }
  }, [selectedtask, setTaskData]);

  // The toolbar's "N tasks" badge, the "Showing x-y of N" text and the pager all read the
  // backend's totalSize (task/list/search) for every role, so the three always agree.
  const effectiveTotalItems = totalPageSize;
  const effectiveStartNumber = effectiveTotalItems === 0 ? 0 : startIndex + 1;
  const effectiveEndNumber = Math.min(endIndex, totalPageSize);
  const effectiveLastPageIndex = Math.max(totalPages, 0);

  useEffect(() => {
    if (typeof setRenderedTaskCount === "function") {
      setRenderedTaskCount(effectiveTotalItems);
    }
  }, [effectiveTotalItems, setRenderedTaskCount]);

  const handleEditTask = (taskId) => {
    navigate(`/update-complete-task/${taskId}`);
  };
  const cancelTooltipClose = useCallback(() => {
    if (tooltipCloseTimerRef.current) {
      clearTimeout(tooltipCloseTimerRef.current);
      tooltipCloseTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => cancelTooltipClose(), [cancelTooltipClose]);

  const handleMouseEnter = useCallback((taskId, e, source = "taskId") => {
    cancelTooltipClose();
    if (isPopupPinned) return;
    setTooltipAnchor(e.currentTarget);
    setHoveredTaskId(taskId);
    setHoveredTooltipSource(source);
  }, [cancelTooltipClose, isPopupPinned]);

  const closeAgentPopup = useCallback(() => {
    cancelTooltipClose();
    setTooltipAnchor(null);
    setHoveredTaskId(null);
    setHoveredTooltipSource(null);
    setIsPopupPinned(false);
  }, [cancelTooltipClose]);

  // Closing is deferred so the pointer can cross the gap between the trigger and
  // the popup -- entering the popup cancels the timer, which is what makes a
  // long (6-9 agent) list scrollable instead of vanishing on the way there.
  const handleMouseLeave = useCallback(() => {
    cancelTooltipClose();
    if (isPopupPinned) return;
    tooltipCloseTimerRef.current = setTimeout(closeAgentPopup, AGENT_TOOLTIP_CLOSE_DELAY_MS);
  }, [cancelTooltipClose, closeAgentPopup, isPopupPinned]);

  const handleCapIconClick = useCallback((taskId, e, source = "taskId") => {
    e.stopPropagation();
    cancelTooltipClose();
    // If already pinned for this specific task, clicking again toggles it off
    if (isPopupPinned && hoveredTaskId === taskId && hoveredTooltipSource === source) {
      closeAgentPopup();
      return;
    }
    setTooltipAnchor(e.currentTarget);
    setHoveredTaskId(taskId);
    setHoveredTooltipSource(source);
    setIsPopupPinned(true);
  }, [cancelTooltipClose, closeAgentPopup, isPopupPinned, hoveredTaskId, hoveredTooltipSource]);

  const renderAgentPopup = (task, source) =>
    tooltipAnchor && hoveredTaskId === task.taskId && hoveredTooltipSource === source ? (
      <AgentHoverPopup
        anchorEl={tooltipAnchor}
        task={task}
        langMode={langMode}
        onMouseEnter={cancelTooltipClose}
        onMouseLeave={handleMouseLeave}
        onDismiss={closeAgentPopup}
      />
    ) : null;

  const handleInputChange = async (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    setAccountNo(nextValue);
    persistTaskSearchValue(nextValue);
  };


  const handleAccountNoSearch = () => {
    const nextValue = inputValue;
    setAccountNo(nextValue);
    persistTaskSearchValue(nextValue);
    setCurrentPage(0);
    setDebouncedTextFilters((prev) => ({
      ...prev,
      accountNo: nextValue,
      inputFilters: String(nextValue ?? "").trim() ? { "Account No.": nextValue } : {},
    }));
  };

  const handleTaskIdInputChange = (event) => {
    const nextValue = event.target.value;
    setTaskIdInputValue(nextValue);
    setTaskIdFilter(nextValue);
  };

  const handleTaskIdSearch = () => {
    const nextValue = taskIdInputValue;
    setTaskIdFilter(nextValue);
    setCurrentPage(0);
    setDebouncedTextFilters((prev) => ({
      ...prev,
      taskIdFilter: nextValue,
      inputFilters: String(nextValue ?? "").trim() ? { taskId: nextValue } : {},
    }));
  };

  const handleSetMapListFilters = (key, value) => {
    if (!key) {
      console.error("Error: Key is required to set the filter.");
      return;
    }

    try {
      let mapListData = Cookies.get("mapListFilters");

      if (mapListData) {
        try {
          mapListData = JSON.parse(mapListData);
        } catch (parseError) {
          console.error("Invalid JSON in mapListFilters cookie:", parseError);
          mapListData = {}; // Reset if parsing fails
        }
      } else {
        mapListData = {};
      }
      if (typeof mapListData !== "object" || mapListData === null) {
        mapListData = {};
      }

      mapListData[key] = value;

      Cookies.set("mapListFilters", JSON.stringify(mapListData));
    } catch (error) {
      console.error("Error updating mapListFilters cookie:", error);
    }
  };


  const handleAreaCodeChange = (event) => {
    const value = event.target.value;
    setAreaInputValue(value);
    setAreaCode(value);
    persistTaskAreaCodeValue(value);
    handleSetMapListFilters("areaInput", value);
  };

  const handleAreacodeSearch = () => {
    const nextValue = areainputValue;
    setAreaCode(nextValue);
    persistTaskAreaCodeValue(nextValue);
    handleSetMapListFilters("areaInput", nextValue);
    setCurrentPage(0);
    setDebouncedTextFilters((prev) => ({
      ...prev,
      areaCode: nextValue,
      inputFilters: String(nextValue ?? "").trim() ? { "Area Code": nextValue } : {},
    }));
  };

  const handlemobileNoChange = (event) => {
    const value = event.target.value;
    setMobileNoInputValue(value);
    handleSetMapListFilters("mobile", value);

    if (value.trim() === "") {
      setMobileNo(value);
    }
  };

  const handlemobileNoSearch = () => {
    const nextValue = mobileNoinputValue;
    setMobileNo(nextValue);
    handleSetMapListFilters("mobile", nextValue);
    setCurrentPage(0);
    setDebouncedTextFilters((prev) => ({
      ...prev,
      mobileNo: nextValue,
      inputFilters: String(nextValue ?? "").trim() ? { "Mobile No": nextValue } : {},
    }));
  };

  const MobileSearchButton = ({ onClick, label }) => (
    <button
      type="button"
      onClick={onClick}
      className="sm:hidden inline-flex items-center justify-center h-8 w-8 rounded-lg border border-slate-200 bg-slate-50 p-2 text-slate-500 hover:bg-[#f1f5f9] active:bg-[#e2e8f0]"
      aria-label={label}
      title={label}
    >
      <Search size={16} />
    </button>
  );

  // Fields already backed by their own dedicated state/handlers (taskId, Account No.,
  // Area Code, created_By and, on project 19, Mobile No) send tightly-scoped params
  // (or the top-level accountNo/areaCode query params) rather than the generic
  // searchFilters bucket, so the API-driven list below excludes their canonical keys
  // to avoid rendering the same field twice with two different code paths.
  const HANDLED_SEARCH_FIELD_CANONICAL_KEYS = useMemo(
    () => new Set(["taskId", "Account No.", "Area Code", "created_By", "Mobile No", TASK_LOCATION_KEY].map(getCanonicalHeaderKey)),
    [getCanonicalHeaderKey]
  );

  // Field list for the toolbar's "Find a field..." picker, driven by
  // /api/v1/task/list/columns so it reflects whatever fields this workflow
  // actually exposes instead of a hardcoded set.
  useEffect(() => {
    if (!activityId) {
      setWorkflowColumns([]);
      setWorkflowColumnsLoaded(false);
      return;
    }
    let cancelled = false;
    const currentUserType = getCurrentUserType?.();
    fetchTaskListColumns(activityId, currentUserType)
      .then((columns) => {
        if (!cancelled) {
          const list = Array.isArray(columns) ? columns : [];
          if (process.env.NODE_ENV !== "production") {
            // Column access is what decides whether a column renders and whether
            // its filter is allowed into the payload, and it varies by user type.
            // Print what the API actually said so a "the filter does nothing"
            // report can be traced to a specific column decision.
            console.info(
              `[task-list] /list/columns userType=${currentUserType} workflowIds=${activityId} -> ${list.length} columns`,
              list.map((col) => ({ key: col.key, accessible: col.accessible }))
            );
          }
          setWorkflowColumns(list);
          setWorkflowColumnsLoaded(true);
        }
      })
      .catch((error) => {
        console.error("Failed to load task search fields:", error);
        if (!cancelled) {
          setWorkflowColumns([]);
          setWorkflowColumnsLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  // Exposes the existing per-field search state/handlers upward (mirrors the
  // onTableHeadersChange pattern) so the toolbar's single search box can drive
  // any of these fields without duplicating filtering logic.
  useEffect(() => {
    if (typeof onSearchFieldsReady !== "function") return;
    if (noColumnAccess) {
      onSearchFieldsReady([]);
      return;
    }
    const headerLabel = (key, fallback) => tableHeaders.find((h) => h.value === key)?.label || fallback;

    /* The fixed columns below keep their dedicated state (the Map screen shares
       accountNo/areaCode through AppContext, and both are persisted across
       reloads), but they now ALSO write the search box value into inputFilters,
       exactly like a dynamic task_details_json column does.

       Before this, a fixed column's value reached the request only via its
       AppContext value while a dynamic column's went through inputFilters --
       two different routes into the same payload. Routing both through
       inputFilters means a granted fixed column filters identically to a
       dynamic one, and the search box behaves the same whichever field is
       picked. Only one search field is active at a time, so replacing the whole
       inputFilters object mirrors the dynamic handler's semantics. */
    const setSearchColumnValue = (columnKey, text) =>
      setInputFilters(String(text ?? "").trim() ? { [columnKey]: text } : {});

    const fields = [
      ...(isAccessibleColumn("taskId")
        ? [{
            key: "taskId",
            label: headerLabel("taskId", "Task ID"),
            value: taskIdInputValue,
            onChange: (text) => {
              handleTaskIdInputChange({ target: { value: text } });
              setSearchColumnValue("taskId", text);
            },
            onSearch: handleTaskIdSearch,
            clear: () => {
              setTaskIdInputValue("");
              setTaskIdFilter("");
              setInputFilters({});
            },
          }]
        : []),
      ...(isAccessibleColumn("Account No.")
        ? [{
            key: "Account No.",
            label: headerLabel("Account No.", "Account No."),
            value: inputValue,
            onChange: (text) => {
              handleInputChange({ target: { value: text } });
              setSearchColumnValue("Account No.", text);
            },
            onSearch: handleAccountNoSearch,
            clear: () => {
              setInputValue("");
              setAccountNo("");
              persistTaskSearchValue("");
              setInputFilters({});
            },
          }]
        : []),
      ...(isAccessibleColumn("Area Code")
        ? [{
            key: "Area Code",
            label: headerLabel("Area Code", "Area Code"),
            value: areainputValue,
            onChange: (text) => {
              handleAreaCodeChange({ target: { value: text } });
              setSearchColumnValue("Area Code", text);
            },
            onSearch: handleAreacodeSearch,
            clear: () => {
              setAreaInputValue("");
              setAreaCode("");
              persistTaskAreaCodeValue("");
              handleSetMapListFilters("areaInput", "");
              setInputFilters({});
            },
          }]
        : []),
      ...(isAccessibleColumn("created_By")
        ? [{
            key: "created_By",
            label: headerLabel("created_By", "Created By"),
            value: createdByFilter,
            onChange: (text) => {
              setCreatedByFilter(text);
              setSearchColumnValue("created_By", text);
            },
            onSearch: () => {},
            clear: () => {
              setCreatedByFilter("");
              setInputFilters({});
            },
          }]
        : []),
      ...(projectId === 19 && isAccessibleColumn("Mobile No")
        ? [{
            key: "Mobile No",
            label: headerLabel("Mobile No", "Mobile No"),
            value: mobileNoinputValue,
            onChange: (text) => {
              handlemobileNoChange({ target: { value: text } });
              setSearchColumnValue("Mobile No", text);
            },
            onSearch: handlemobileNoSearch,
            clear: () => {
              setMobileNoInputValue("");
              setMobileNo("");
              handleSetMapListFilters("mobile", "");
              setInputFilters({});
            },
          }]
        : []),
      // Remaining fields mirror the table's own column list (tableHeaders) --
      // the same already-deduped, access-filtered list that drives both the
      // table and the "Columns" picker -- instead of the raw workflow-columns
      // metadata, which could include extra/duplicate/inaccessible entries
      // that never actually render as real table columns.
      ...tableHeaders
        .filter((header) => header?.value && !HANDLED_SEARCH_FIELD_CANONICAL_KEYS.has(getCanonicalHeaderKey(header.value)))
        .map((header) => ({
          key: header.value,
          label: header.label || header.value,
          value: inputFilters?.[header.value] || "",
          onChange: (text) => setInputFilters(String(text ?? "").trim() ? { [header.value]: text } : {}),
          onSearch: () => {
            const val = inputFilters?.[header.value] || "";
            setCurrentPage(0);
            setDebouncedTextFilters((prev) => ({
              ...prev,
              inputFilters: String(val ?? "").trim() ? { [header.value]: val } : {},
            }));
          },
          clear: () => setInputFilters((prev) => ({ ...(prev || {}), [header.value]: "" })),
        })),
    ];
    onSearchFieldsReady(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskIdInputValue, inputValue, areainputValue, createdByFilter, mobileNoinputValue, tableHeaders, projectId, onSearchFieldsReady, inputFilters, HANDLED_SEARCH_FIELD_CANONICAL_KEYS, getCanonicalHeaderKey, noColumnAccess, isAccessibleColumn]);

  const handleAssignTaskAgent = (event) => {
    const nextAssignedUserId = event?.value ?? "";
    setAssignTaskAgent(nextAssignedUserId);
    setSelectedUserId(nextAssignedUserId);
    handleSetMapListFilters("selectedUserId", nextAssignedUserId);
  };


  const handlePrioritySelection = (selectedValue) => {
    const selectedOption = priorityoptions.find(
      (option) => option.value === selectedValue?.value
    );
    console.log("selectedValue", selectedValue, "====", selectedOption)
    if (selectedOption) {
      const nextPriority = normalizePriorityFilterValue(selectedOption.value);
      setPriority(nextPriority);
      persistTaskManageFiltersPatch({ priorityFilter: nextPriority });
      if (selectedOption && selectedOption.value) {
        handleSetMapListFilters("selectedPriority", selectedOption.value);
      } else {
        console.warn("selectedOption or selectedOption.id is undefined:", selectedOption);
      }

    }
  };

  const handleStatusSelection = (selectedValue) => {
    const selectedOption = activeoptions.find(
      (option) => option.value === selectedValue.value
    );
    if (selectedOption) {
      const nextTaskStatus = normalizeTaskStatusFilterValue(selectedOption.value);
      setFinishedStatus(nextTaskStatus);
      setActive(nextTaskStatus);
      persistTaskManageFiltersPatch({ taskStatusFilter: nextTaskStatus });
      handleSetMapListFilters("selectedStatus", selectedOption.value)
    }
  };

  // const formatDate = (dateString) => {
  //   const date = new Date(dateString);
  //   const year = date.getFullYear();
  //   const month = `0${date.getMonth() + 1}`.slice(-2);
  //   const day = `0${date.getDate()}`.slice(-2);
  //   return `${year}-${month}-${day}`;
  // };

  const handleOpenPDF = async (taskId) => {
    try {
      const data = await fetchPdfDataTaskList(taskId);

      if (data === null || data === undefined) {
        toast.error(translations[langMode].pdfnotavailable);
        return;
      }
      setSelectedPdfLink(data);
      setOpen(true);

    } catch (error) {
      console.error("Error fetching PDF data:", error);
      toast.error(error?.response?.data?.body?.value || error?.body?.value || translations[langMode].pdfnotavailable);
    }
  }

  const fetchTaskHistory = async (accountNumber) => {
    setIsLoading(true);
    try {

      const responseData = await fetchAccountHistory(
        accountNumber,
        null,
        isSuperAdminUser ? selectedOrgId : null
      );

      if (responseData?.header?.code === 600) {
        setAccountHistory(normalizeAccountHistoryValue(responseData?.body?.value));
        toast.success(translations[langMode].datafetchedsuccessfully);
      } else {
        toast.error(responseData.body.value);
      }
    } catch (error) {
      console.error("Error fetching task history:", error);
      toast.error(translations[langMode].anerroroccuredtaskhistory);
    } finally {
      setIsLoading(false);
    }
  };


  // Utility functions for status classes and icons
  function getStatusBadgeClass(status) {
    switch (status) {
      case "Active":
        return "bg-blue-100 text-blue-700 border border-blue-400";
      case "Assigned":
        return "bg-yellow-100 text-yellow-700 border border-yellow-400";
      case "On_Hold":
        return "bg-red-100 text-red-600 border border-red-400";
      case "Completed":
        return "bg-green-100 text-green-700 border border-green-400";
      case "In_Progress":
        return "bg-orange-100 text-orange-700 border border-orange-400";
      case "Pending":
        return "bg-yellow-50 text-yellow-800 border border-yellow-300";
      case "Finished":
        return "bg-emerald-100 text-emerald-700 border border-emerald-400";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-300";
    }
  }

  function getTaskStatusIcon(status) {
    switch (status) {
      case "Assigned":
        return <HardHat className="text-yellow-400" />;
      case "Completed":
        return <HardHat className="text-green-500" />;
      case "On_Hold":
        return <HardHat className="text-red-500" />;
      case "In_Progress":
        return <HardHat className="text-orange-500" />;
      default:
        return null;
    }
  }

  const formattedDate = (dateString) => {
    if (!dateString) {
      return "-";
    }
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true, // Use 12-hour format with AM/PM
    };

    const timeZone = dateString.match(/([+\-]\d{2}:\d{2})$/)?.[1] || 'UTC';
    options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };

  const formatDate = (date) => {
    return date ? date.toISOString().split("T")[0] : "";
  };

  const extraTableColumnsCount = 2 + (showTaskActionColumn ? 1 : 0); // checkbox + NO. + optional Action
  const tableViewportRef = useRef(null);
  const tableRef = useRef(null);
  const [tableViewportWidth, setTableViewportWidth] = useState(0);
  const [tableViewportScrollLeft, setTableViewportScrollLeft] = useState(0);
  const [renderedTableWidth, setRenderedTableWidth] = useState(0);

  useEffect(() => {
    const viewportElement = tableViewportRef.current;
    if (!viewportElement) return undefined;

    const updateViewportWidth = () => {
      setTableViewportWidth(viewportElement.clientWidth || 0);
    };
    const updateRenderedTableWidth = () => {
      const width = tableRef.current?.getBoundingClientRect?.().width || 0;
      setRenderedTableWidth(width);
    };
    const updateScrollLeft = () => {
      setTableViewportScrollLeft(viewportElement.scrollLeft || 0);
    };

    updateViewportWidth();
    updateRenderedTableWidth();
    updateScrollLeft();
    viewportElement.addEventListener("scroll", updateScrollLeft, { passive: true });

    if (typeof ResizeObserver !== "undefined") {
      const resizeObserver = new ResizeObserver(() => {
        updateViewportWidth();
        updateRenderedTableWidth();
      });
      resizeObserver.observe(viewportElement);
      if (tableRef.current) {
        resizeObserver.observe(tableRef.current);
      }
      return () => {
        resizeObserver.disconnect();
        viewportElement.removeEventListener("scroll", updateScrollLeft);
      };
    }

    window.addEventListener("resize", updateViewportWidth);
    return () => {
      window.removeEventListener("resize", updateViewportWidth);
      viewportElement.removeEventListener("scroll", updateScrollLeft);
    };
  }, []);

  const tableMinWidthPx = useMemo(() => {
    const leadingColumnsWidth = 60 + 60 + (showTaskActionColumn ? 108 : 0);
    const dynamicColumnsWidth = (visibleTableHeaders || []).reduce(
      (sum, header) => sum + getTableColumnMinWidthPx(header),
      0
    );
    return leadingColumnsWidth + dynamicColumnsWidth;
  }, [showTaskActionColumn, visibleTableHeaders]);
  const unusedTableWidthPx = Math.max(0, tableViewportWidth - tableMinWidthPx);
  const effectiveRenderedTableWidthPx = Math.max(0, renderedTableWidth || tableMinWidthPx);
  const availableRightGapPx = Math.max(0, tableViewportWidth - effectiveRenderedTableWidthPx);
  const HIDDEN_COLUMNS_PLACEHOLDER_MIN_WIDTH_PX = 360;
  const HIDDEN_COLUMNS_CHIP_MIN_WIDTH_PX = 150;
  const placeholderWidthPx = Math.max(0, Math.min(unusedTableWidthPx, availableRightGapPx));
  const visibleDataColumnCountForWidth = (visibleTableHeaders || []).length;
  const shouldShowHiddenColumnsAssist =
    Math.abs(tableViewportScrollLeft) <= 1 &&
    !!activityId &&
    !effectiveLoading &&
    (displayTaskData || []).length > 0;
  const showHiddenColumnsPlaceholder =
    shouldShowHiddenColumnsAssist &&
    (
      (visibleDataColumnCountForWidth === 1 && placeholderWidthPx >= HIDDEN_COLUMNS_CHIP_MIN_WIDTH_PX) ||
      (visibleDataColumnCountForWidth > 1 && placeholderWidthPx >= HIDDEN_COLUMNS_PLACEHOLDER_MIN_WIDTH_PX)
    );
  const showHiddenColumnsChip =
    shouldShowHiddenColumnsAssist &&
    visibleDataColumnCountForWidth > 1 &&
    !showHiddenColumnsPlaceholder &&
    placeholderWidthPx >= HIDDEN_COLUMNS_CHIP_MIN_WIDTH_PX;
  const showRightFillerColumn =
    (visibleTableHeaders || []).length === 1 || showHiddenColumnsPlaceholder;
  const totalTableColumnsCount =
    visibleTableHeaders.length +
    extraTableColumnsCount +
    (showRightFillerColumn ? 1 : 0);
  const shouldFillViewportWidth =
    visibleDataColumnCountForWidth > 1 &&
    visibleDataColumnCountForWidth <= 5;
  const tableRenderWidthPx = shouldFillViewportWidth
    ? Math.max(tableMinWidthPx, tableViewportWidth || 0)
    : tableMinWidthPx;

  const STATIC_FILTER_COLUMN_KEYS = ["taskId", "Account No.", "History", "Activity", "priority", "completedDate", "taskStatus", "source", "Area Code", "created_By", "createdDate", "assignedTo", "taskCategory", "Comment", "Mobile No"];

  // Reads the stored filter for a header, whatever op it uses.
  const getColumnFilter = (header) => columnFilters?.[columnKeyForHeader(header?.value)];

  const hasActiveColumnFilter = (header) => {
    if (isDateColumn(header) && header.value === "createdDate") {
      // createdDate is driven by the toolbar's shared from/to range.
      return Boolean(fromDate || toDate);
    }
    return isMeaningfulColumnFilter(getColumnFilter(header));
  };

  /* Writes a funnel selection into the filter model. Each of these replaces the
     column's entry outright, and removes it when the selection is empty -- an
     empty picker means "don't filter this column", never "match nothing".

     Also the one place the MAX_COLUMN_FILTERS cap is enforced in the UI: the
     backend rejects a payload with more than 15 with header.code 601, and
     serializeColumnFilters would otherwise trim the overflow silently, leaving
     the user with a funnel that looks applied but isn't. Refuse the extra
     column up front and say why instead.

     The cap is counted over activeColumnFilters, not the funnel model alone,
     because toolbar boxes and the funnels merge into the same payload. Editing
     or clearing a column that is already filtered can't grow that payload, so
     only genuinely new columns are blocked. */
  const setColumnFilterEntry = (header, filter) => {
    const colKey = columnKeyForHeader(header?.value);
    if (!colKey) return;

    // Checks both models: Assigned To lives only in columnFilters (it leaves
    // as assigneeIds, so it never appears in activeColumnFilters) and must not
    // be mistaken for a brand-new column every time it is edited.
    const isAddingNewColumn =
      isMeaningfulColumnFilter(filter) &&
      !activeColumnFilters[colKey] &&
      !columnFilters[colKey];
    if (isAddingNewColumn && activeColumnFilterCount >= MAX_COLUMN_FILTERS) {
      showAlert({
        variant: "warning",
        title: translations[langMode]?.maxFiltersReachedTitle ?? "Filter limit reached",
        message: (
          translations[langMode]?.maxFiltersReachedMsg ??
          "You can apply up to {limit} filters at a time. Clear one before adding another."
        ).replace("{limit}", MAX_COLUMN_FILTERS),
      });
      return;
    }

    setColumnFilters((prev) => {
      const next = { ...(prev || {}) };
      // Same reason as put(): stamp the metadata-resolved key on the entry so
      // the stored column name is the one the endpoint expects.
      const resolved = { ...filter, column: colKey };
      if (isMeaningfulColumnFilter(resolved)) next[colKey] = resolved;
      else delete next[colKey];
      return next;
    });
  };

  const applyColumnFilterSelection = (header, nextSelected) => {
    setColumnFilterEntry(header, makeInFilter(header?.value, nextSelected));
  };

  const applyColumnContainsFilter = (header, nextValue) => {
    setColumnFilterEntry(header, makeContainsFilter(header?.value, nextValue));
  };

  // Columns whose row-2 predecessor rendered a real control (kept here 1:1).
  const STATIC_FILTERABLE_KEYS = ["taskId", "Account No.", "priority", "taskStatus", "source", "Area Code", "created_By", "createdDate", "assignedTo", "Mobile No"];
  // Columns that intentionally had no filter control before (computed/derived fields).
  const STATIC_NO_FILTER_KEYS = ["History", "Activity", "completedDate", "taskCategory", "Comment"];

  const isColumnFilterable = (header) => {
    if (!header?.value || header.value === "History" || header.value === TASK_LOCATION_KEY) return false;
    return true;
  };


  const DateColumnFilter = ({
    header,
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    columnFilters,
    setColumnFilterEntry,
    closePopover,
  }) => {
    const colKey = header.value;

    const getRange = () => {
      if (colKey === "createdDate") {
        return [fromDate || "", toDate || ""];
      }
      const stored = columnFilters?.[columnKeyForHeader(colKey)];
      if (stored?.op === "between") {
        return [stored.from || "", stored.to || ""];
      }
      return ["", ""];
    };

    const [currentFrom, currentTo] = getRange();

    const [inputText, setInputText] = useState(
      currentFrom || currentTo
        ? `${currentFrom || "—"} to ${currentTo || "—"}`
        : ""
    );

    useEffect(() => {
      const matches = inputText.match(/\d{4}-\d{2}-\d{2}/g) || [];
      const currentStart = matches[0] || "";
      const currentEnd = matches[1] || "";
      if (currentStart !== currentFrom || currentEnd !== currentTo) {
        setInputText(
          currentFrom || currentTo
            ? `${currentFrom || "—"} to ${currentTo || "—"}`
            : ""
        );
      }
    }, [currentFrom, currentTo]);

    const isValidDateStr = (s) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
      const parts = s.split("-").map(Number);
      const d = new Date(parts[0], parts[1] - 1, parts[2]);
      return d.getFullYear() === parts[0] && d.getMonth() === parts[1] - 1 && d.getDate() === parts[2];
    };

    const handleInputChange = (e) => {
      const val = e.target.value;
      setInputText(val);

      if (!val.trim()) {
        updateRange("", "");
        return;
      }

      const matches = val.match(/\d{4}-\d{2}-\d{2}/g) || [];
      const parsedStart = matches[0] || "";
      const parsedEnd = matches[1] || "";

      let nextFrom = currentFrom;
      let nextTo = currentTo;

      if (parsedStart && isValidDateStr(parsedStart)) {
        nextFrom = parsedStart;
      } else if (!parsedStart) {
        nextFrom = "";
      }

      if (parsedEnd && isValidDateStr(parsedEnd)) {
        nextTo = parsedEnd;
      } else if (!parsedEnd) {
        nextTo = "";
      }

      if (nextFrom !== currentFrom || nextTo !== currentTo) {
        updateRange(nextFrom, nextTo);
      }
    };

    const updateRange = (start, end) => {
      if (colKey === "createdDate") {
        setFromDate(start);
        setToDate(end);
        return;
      }
      // "between" is inclusive and either bound is optional; dropping the
      // entry entirely when both are empty keeps the column unfiltered.
      // Goes through setColumnFilterEntry so a date range counts against the
      // same 15-filter cap as every other funnel.
      setColumnFilterEntry(header, makeBetweenFilter(colKey, start, end));
    };

    const parseLocalYmd = (s) => {
      if (!s) return null;
      const parts = String(s).split("-").map(Number);
      if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
      return new Date(parts[0], parts[1] - 1, parts[2]);
    };

    const formatLocalYmd = (date) => {
      if (!date) return "";
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    return (
      <div className="w-full flex flex-col items-center">
        <div className="w-full relative mb-2 text-left">
          <input
            type="text"
            value={inputText}
            onChange={handleInputChange}
            placeholder={translations[langMode]?.selectDateRangeHint ?? "Select date range (YYYY-MM-DD to YYYY-MM-DD)"}
            className="w-full min-w-0 h-8 text-xs border rounded-lg px-2.5 bg-slate-50 placeholder:text-slate-400 text-slate-700 font-medium border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
          />
        </div>
        <div className="react-datepicker-inline-container border border-slate-100 rounded-lg overflow-hidden shadow-sm">
          <DatePicker
            selected={null}
            startDate={currentFrom ? parseLocalYmd(currentFrom) : null}
            endDate={currentTo ? parseLocalYmd(currentTo) : null}
            onChange={(dates) => {
              const [start, end] = dates || [];
              updateRange(start ? formatLocalYmd(start) : "", end ? formatLocalYmd(end) : "");
            }}
            selectsRange
            inline
          />
        </div>
        <div className="w-full flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={() => {
              updateRange("", "");
            }}
            className="text-[11px] font-medium text-slate-400 hover:text-slate-600 hover:underline transition-colors"
          >
            {translations[langMode]?.clear ?? "Clear"}
          </button>
          <button
            type="button"
            onClick={() => {
              closePopover?.();
            }}
            className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded-md hover:bg-[#1d5a96] transition-all shadow-sm"
          >
            {translations[langMode]?.apply ?? "Apply"}
          </button>
        </div>
      </div>
    );
  };


  const renderPrototypeColumnFilter = (header, closePopover) => {
    if (isDateColumn(header)) {
      return (
        <DateColumnFilter
          header={header}
          fromDate={fromDate}
          toDate={toDate}
          setFromDate={setFromDate}
          setToDate={setToDate}
          columnFilters={columnFilters}
          setColumnFilterEntry={setColumnFilterEntry}
          closePopover={closePopover}
        />
      );
    }

    // Every column gets the distinct-value checkbox list. priority /
    // taskStatus / source are answered from their fixed enums with no request;
    // the rest come from POST list/column-values. If that yields nothing (the
    // column isn't supported yet), the picker falls back to a `contains` box
    // internally so the column is still filterable server-side.
    const stored = columnFilters?.[columnKeyForHeader(header?.value)];
    return (
      <ColumnValueFilterList
        workflowIds={activityId}
        column={header?.value}
        // Assigned To is filtered by assignee ID, not by name -- the API takes
        // assigneeIds, and two agents can share a display name. The picker
        // lists the workflow's field agents (already narrowed to the groups
        // this user can see) and stores their IDs.
        staticOptions={isAssignedToColumnKey(header?.value) ? visibleAgentOptions : null}
        columnConditions={toColumnConditions(
          serializedColumnFilters,
          columnKeyForHeader(header?.value)
        )}
        selected={stored?.op === "in" ? stored.values : []}
        containsValue={stored?.op === "contains" ? stored.value : ""}
        onApply={(nextSelected) => {
          applyColumnFilterSelection(header, nextSelected);
          closePopover?.();
        }}
        onApplyContains={(nextValue) => {
          applyColumnContainsFilter(header, nextValue);
          closePopover?.();
        }}
      />
    );
  };


  return (
    <>
      <div
	        className={`task-list-table mx-2 mb-2 mt-2 flex min-h-[24rem] min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-3 sm:mb-2 sm:mt-2 sm:min-h-0 sm:flex-1 ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        {/* Showing X-Y of Z bar + filters row - compact */}
        {!noColumnAccess && (
	        <div className={`scrollbar-container relative z-30 overflow-visible flex flex-wrap items-center justify-between px-3 py-2.5 border-b border-slate-100 bg-white sm:px-5 ${theme === "dark" ? "border-gray-600" : ""}`}
          style={{ zIndex: 30 }}
        >

	          <div className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2 sm:mx-auto sm:mt-1 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-between">
            {/* resolved length error by harshit */}
            {!taskData?.length <= 0 && (
              <>
                {/* resolved length error by harshit */}
                <div>
	                  <p className="flex items-center gap-1.5 text-xs text-slate-500 sm:me-2 sm:gap-4 sm:whitespace-nowrap">
                    <span className="whitespace-nowrap">{`${translations[langMode]?.showing ?? "Showing"} ${effectiveStartNumber}-${effectiveEndNumber} ${translations[langMode]?.of ?? "of"} ${effectiveTotalItems}`}</span>
                  </p>
                </div>
	                <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-2 text-xs text-slate-500 sm:ml-auto sm:flex-nowrap sm:gap-3">
                  <div className="flex items-center gap-1.5">
	                    <label htmlFor="pageSize" className="block whitespace-nowrap leading-tight">{translations[langMode].rows}</label>
                    <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} disabled={effectiveLoading} height="32px" fontSize="12px" />
                  </div>
                  {/* Paging is disabled while a search is in flight: each page
                      re-runs the filter over the whole workflow, so queueing
                      clicks just stacks up work whose results get discarded. */}
                  <span className="pager-nav flex items-center gap-1">
                    <PagerArrowButton
                      direction="prev"
                      isRtl={isRtl}
                      onClick={goToPreviousPage}
                      disabled={effectiveLoading || currentPage === 0}
                      ariaLabel={translations[langMode]?.previousPage || "Previous page"}
                    />
                    <span className="text-xs text-slate-600 px-1 whitespace-nowrap">{currentPage + 1} / {effectiveLastPageIndex + 1}</span>
                    <PagerArrowButton
                      direction="next"
                      isRtl={isRtl}
                      onClick={goToNextPage}
                      disabled={effectiveLoading || currentPage === effectiveLastPageIndex}
                      ariaLabel={translations[langMode]?.nextPage || "Next page"}
                    />
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
        )}
        {effectiveLoading && <Spinner />}
	        <div
            ref={tableViewportRef}
            onDragOver={handleTableDragOver}
            className="scrollbar-container task-list-scrollbar relative min-h-0 w-full flex-1 overflow-x-auto overflow-y-auto pb-3"
            style={{ scrollbarGutter: "stable" }}
          >
          <table
              ref={tableRef}
              className={`table-fixed text-xs text-center text-gray-500 dark:text-gray-400 relative z-0 border-collapse ${noColumnAccess ? "h-full" : ""}`}
              dir={dir}
              style={{
                width: `${tableRenderWidthPx}px`,
                minWidth: `${tableMinWidthPx}px`,
              }}
            >
            {!noColumnAccess && (
            <thead className="text-xs text-slate-700 border-b border-slate-200 dark:text-gray-400 sticky top-0 bg-slate-50 dark:bg-gray-800 z-20">

              <tr className={theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}>
                <th className="w-[60px] min-w-[60px] max-w-[60px] text-xs font-semibold uppercase py-3.5 px-0 text-gray-800 text-center bg-slate-50 dark:bg-gray-800">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-slate-300 accent-[#236DB4] text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    checked={selectAll}
                    disabled={effectiveLoading}
                    onChange={handleSelectAllChange}
                    title={effectiveLoading ? (translations[langMode]?.loadingTasks ?? "Loading tasks...") : undefined}
                  />
                </th>
                <th className="w-[60px] min-w-[60px] max-w-[60px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-400 tracking-[0.04em] text-center bg-slate-50 dark:bg-gray-800">
                  NO.
                </th>

                {showTaskActionColumn && (
                  <th className="w-[108px] min-w-[108px] max-w-[108px] text-xs font-semibold uppercase py-3.5 px-0 text-slate-500 tracking-[0.04em] text-center bg-slate-50 dark:bg-gray-800">
                    {translations[langMode].Action}
                  </th>
                )}
                {orderedVisibleTableHeaders.map((header) => (
                  <th
                    key={header.value}
                    onDragOver={(e) => handleColumnDragOver(e, header.value)}
                    onDrop={(e) => handleColumnDrop(e, header.value)}
                    style={{
                      width: colWidths[header.value] || getTableColumnMinWidthPx(header),
                      minWidth: colWidths[header.value] || getTableColumnMinWidthPx(header),
                      maxWidth: colWidths[header.value] || getTableColumnMinWidthPx(header),
                    }}
                    className={`relative ${getDataCellPaddingXClass()} py-3.5 text-xs font-semibold uppercase text-slate-500 tracking-[0.04em] text-left transition-colors bg-slate-50 dark:bg-gray-800 ${colWidths[header.value] ? "" : getTableColumnWidthClass(header)} ${dragOverColKey === header.value && dragColKey && dragColKey !== header.value ? "bg-blue-100" : ""} ${dragColKey === header.value ? "opacity-40" : ""}`}
                  >
                    <span className="inline-flex items-center justify-start gap-0.5 max-w-full">
                      <span
                        draggable
                        onDragStart={(e) => handleColumnDragStart(e, header.value)}
                        onDragEnd={handleColumnDragEnd}
                        className="flex items-center gap-0.5 min-w-0 cursor-grab active:cursor-grabbing"
                        title={translations[langMode]?.dragToReorderColumn ?? "Drag to reorder column"}
                      >
                        <GripVertical size={11} className="text-slate-300 shrink-0" />
                        <OverflowTooltipText text={header?.label || ""} />
                      </span>
                      {showTableFilters && isColumnFilterable(header) && (
                        <span className={justRevealedFilters ? "inline-flex animate-filter-pulse" : "inline-flex"}>
                          <HeaderFilterPopover
                            active={hasActiveColumnFilter(header)}
                            width={isDateColumn(header) ? 300 : 232}
                            tooltipText={`${translations[langMode]?.filterByColumn ?? "Filter by"} ${header?.label || ""}`}
                          >
                            {(closePopover) => renderPrototypeColumnFilter(header, closePopover)}
                          </HeaderFilterPopover>
                        </span>
                      )}
                    </span>
                    <span
                      onMouseDown={(e) => startColumnResize(e, header.value, colWidths[header.value] ?? e.currentTarget.parentElement.getBoundingClientRect().width)}
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize select-none group flex justify-center z-10"
                      title={translations[langMode]?.dragToResize ?? "Drag to resize"}
                    >
                      <span className="w-px h-full bg-transparent group-hover:bg-blue-400" />
                    </span>
                  </th>
                ))}
                {showRightFillerColumn && <th aria-hidden="true" className="w-auto px-0 py-3" />}
              </tr>

            </thead>
            )}
            <tbody>
              {effectiveLoading ? (
                <tr>
                  <td colSpan={totalTableColumnsCount} className="h-[280px] min-h-[280px] p-0 align-middle">
                    {/* Sticky (not absolute-within-td) so this stays inside the visible
                        scroll viewport -- the td itself spans every column via colSpan,
                        which can be far wider than the viewport once several columns are
                        shown, and centering directly in that wide td put the spinner off
                        to the right where it wasn't visible without scrolling. */}
                    <div
                      className="sticky left-0 flex h-[280px] items-center justify-center bg-white/80 rounded-lg"
                      style={{ width: tableViewportWidth ? `${tableViewportWidth}px` : "100%" }}
                    >
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : (
                <Fragment>
                  {
                    !activityId ?
                      <tr>
                        <td colSpan={totalTableColumnsCount} className="h-[200px] p-0 align-middle">
                          <div
                            className="sticky left-0 flex h-[200px] items-center justify-center text-center text-sm text-slate-400"
                            style={{ width: tableViewportWidth ? `${tableViewportWidth}px` : "100%" }}
                          >
                            {translations[langMode].seeData}
                          </div>
                        </td>
                      </tr>
                      : noColumnAccess ? (
                        <tr className="h-full">
                          <td colSpan={totalTableColumnsCount} className="h-full min-h-[24rem] p-0 align-middle">
                            <div
                              className="sticky left-0 flex h-full min-h-[24rem] items-center justify-center"
                              style={{ width: tableViewportWidth ? `${tableViewportWidth}px` : "100%" }}
                            >
                              <div className="max-w-md rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center">
                                <p className="text-sm font-semibold text-amber-800">
                                  {translations[langMode]?.noColumnAccessTitle ?? "No column access"}
                                </p>
                                <p className="mt-1 text-xs leading-5 text-amber-700">
                                  {translations[langMode]?.noColumnAccessMessage ??
                                    "You do not have access to any columns for this workflow. Please contact your super admin."}
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) :
                      (displayTaskData || [])?.length <= 0 ? (
                        <tr>
                          <td colSpan={totalTableColumnsCount} className="h-[200px] p-0 align-middle">
                            {/* Same sticky trick as the spinner/seeData rows above --
                                keeps the empty-state message inside the visible viewport
                                instead of centered across the full (possibly very wide)
                                colSpan width, which required scrolling right to see it. */}
                            <div
                              className="sticky left-0 flex h-[200px] items-center justify-center text-center text-sm text-slate-400"
                              style={{ width: tableViewportWidth ? `${tableViewportWidth}px` : "100%" }}
                            >
                              {searchErrorCode === TASK_LIST_CODES.ACCESS_DENIED
                                ? (translations[langMode]?.accessDenied ??
                                    "You do not have access to these tasks.")
                                : hasAnyFilterActive
                                  ? (translations[langMode].noFilteredData ?? translations[langMode].noDatavb)
                                  : translations[langMode].noDatavb}
                            </div>
                          </td>
                        </tr>
                      ) :
                        (
                          <>
                            {(displayTaskData || [])?.map((task, index) => {
                            const taskDataIndex = (taskData || []).findIndex((t) => t.taskId === task.taskId);
                            const taskDetailsObj = getSafeTaskDetails(task.taskDetails);
                            return (
                              <tr
                                key={task.taskId ?? index}
                                className={`text-xs border-b border-slate-100 transition-colors duration-150 ${theme === "dark"
                                  ? "dark-mode"
                                  : theme === "high-contrast"
                                    ? "high-contrast"
                                    : "bg-white hover:bg-slate-50/70"
                                  }`}
                              >
                                <td className="w-[60px] min-w-[60px] max-w-[60px] py-2.5 text-center text-[#4B5563]">
                                  <input
                                    type="checkbox"
                                    className={`w-4 h-4 text-blue-800 bg-white border-blue-600 rounded-[3px] focus:ring-blue-800 dark:focus:ring-blue-800 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:border-gray-600 ${theme === "dark"
                                      ? "dark-mode"
                                      : theme === "high-contrast"
                                        ? "high-contrast"
                                        : ""
                                      } ${!isTaskSelectable(task) ? "invisible" : ""}`}
                                    checked={task.isChecked || false}
                                    disabled={!isTaskSelectable(task)}
                                    onChange={() => handleCheckboxChange(taskDataIndex >= 0 ? taskDataIndex : index)}
                                  />
                                </td>
                                <td className="w-[60px] min-w-[60px] max-w-[60px] px-0 py-2.5 text-xs text-[#70788f] whitespace-nowrap font-mono text-center">
                                  {((currentPage + 1) - 1) * pageSize + index + 1}
                                </td>

                                {showTaskActionColumn && (
                                    <td className="w-[108px] min-w-[108px] max-w-[108px] py-2.5 text-center">
                                      <div className="flex items-center justify-center gap-0.5">
                                      <ActionIconButton
                                        onClick={() => handleEditTask(task.taskId)}
                                        tone="blue"
                                        ariaLabel={translations[langMode].edit}
                                        className="!h-7 !w-7 !rounded-md"
                                      >
                                        <Pencil size={14} />
                                      </ActionIconButton>
                                      <ActionIconButton
                                        onClick={() => fetchTaskHistory(task.accountNo)}
                                        tone="blue"
                                        ariaLabel={translations[langMode].history || "History"}
                                        className="!h-7 !w-7 !rounded-md"
                                      >
                                        <History size={14} />
                                      </ActionIconButton>
                                      {task?.taskStatus === "Completed" && (
                                        <ActionIconButton
                                          onClick={() => handleOpenPDF(task.taskId)}
                                          tone="red"
                                          ariaLabel={translations[langMode].pdflink || "Open PDF"}
                                          className="!h-7 !w-7 !rounded-md"
                                        >
                                          <img
                                            src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344905/fileTypeIcon_mmuvfk.png"
                                            alt=""
                                            className="w-4 h-4"
                                          />
                                        </ActionIconButton>
                                      )}
                                      </div>
                                    </td>
                                )}
                                {orderedVisibleTableHeaders.map((header) => (
                                  <td
                                    key={header.value}
                                    style={{
                                      width: colWidths[header.value] || getTableColumnMinWidthPx(header),
                                      minWidth: colWidths[header.value] || getTableColumnMinWidthPx(header),
                                      maxWidth: colWidths[header.value] || getTableColumnMinWidthPx(header),
                                    }}
                                    className={getTableCellClass(header)}
                                  >
                                    {header?.value === "Account No." ? (
                                      <div className="flex items-center justify-start text-xs font-normal">
                                        {task.protected && (
                                          <Shield className="mr-2 text-blue-800" />
                                        )}
                                        {task.accountNo}
                                      </div>

                                    ) : header?.value === "Mobile No" ? (
                                      <span className="block px-2 py-1">
                                        <OverflowTooltipText
                                          text={taskDetailsObj["Mobile No"] ? taskDetailsObj["Mobile No"] : "-"}
                                        />
                                      </span>
                                    ) : header?.value === TASK_LOCATION_KEY ? (
                                      hasValidLocation(task?.latitude, task?.longitude) ? (
                                        <ActionIconButton
                                          onClick={() =>
                                            setTaskLocationView({
                                              latitude: task.latitude,
                                              longitude: task.longitude,
                                              label: task.accountNo ? `${translations[langMode]?.accno ?? "Account No"}: ${task.accountNo}` : undefined,
                                            })
                                          }
                                          tone="blue"
                                          ariaLabel={translations[langMode]?.taskLocationTooltip ?? "View task location on map"}
                                          tooltipText={translations[langMode]?.taskLocationTooltip ?? "View task location on map"}
                                          tooltipPosition="top"
                                          tooltipClassName="w-max max-w-[min(8.5rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                                          className="!h-7 !w-7 !rounded-md"
                                        >
                                          <MapPin size={15} />
                                        </ActionIconButton>
                                      ) : (
                                        <span className="text-slate-300">—</span>
                                      )
                                    ) : header?.value === "History" ? (
                                      <ActionIconButton
                                        onClick={() => fetchTaskHistory(task.accountNo)}
                                        tone="blue"
                                        ariaLabel={translations[langMode].history || "History"}
                                        className="h-8 w-8 border-transparent"
                                      >
                                        <History size={18} />
                                      </ActionIconButton>
                                    )
                                      : header?.value === "created_By" ? (
                                        <OverflowTooltipText text={task?.createdByName || "-"} />
                                      )
                                        : header?.value === "createdDate" ? (
                                          <OverflowTooltipText text={formattedDate(task?.createdDate)} />
                                        )
                                          : header?.value === "completedDate" ? (
                                            <OverflowTooltipText text={task?.taskStatus === "Completed" ? formattedDate(task?.finishedDate) : "-"} />
                                          )
                                            : header?.value === "taskId" ? (
                                              <div className="flex w-full justify-start items-center">
                                                <span
                                                  className={`flex items-center gap-2 bg-opacity-30 px-1 py-1 ${theme === "dark"
                                                      ? "text-white"
                                                      : theme === "high-contrast"
                                                        ? "high-contrast"
                                                        : ""
                                                    }`}
                                                >
                                                  <div
                                                    className="relative group cursor-pointer"
                                                    onMouseEnter={(e) => handleMouseEnter(task.taskId, e, "taskId")}
                                                    onMouseLeave={handleMouseLeave}
                                                    onClick={(e) => handleCapIconClick(task.taskId, e, "taskId")}
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={translations[langMode]?.viewFieldAgents ?? "View Field Agents"}
                                                    onKeyDown={(e) => {
                                                      if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        handleCapIconClick(task.taskId, e, "taskId");
                                                      }
                                                    }}
                                                  >
                                                    <span className="inline-flex h-6 w-6 items-center justify-center" ref={tooltipRef}>
                                                      {task.taskStatus === "Assigned" && (
                                                        <HardHat className="text-yellow-400" />
                                                      )}
                                                      {task.taskStatus === "Completed" && (
                                                        <HardHat className="text-green-500" />
                                                      )}
                                                      {task.taskStatus === "On_Hold" && (
                                                        <HardHat className="text-red-500" />
                                                      )}
                                                      {task.taskStatus === "In_Progress" && (
                                                        <HardHat className="text-orange-500" />
                                                      )}
                                                      {!["Assigned", "Completed", "On_Hold", "In_Progress"].includes(task.taskStatus) && (
                                                        <span className="h-5 w-5" aria-hidden="true" />
                                                      )}
                                                    </span>

                                                  </div>
                                                  <span
                                                    className="pt-[2px] cursor-pointer hover:underline hover:text-[#236DB4] transition-colors"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      closeAgentPopup();
                                                      handleTaskClick(task.taskId, task.accountNo);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                      if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        closeAgentPopup();
                                                        handleTaskClick(task.taskId, task.accountNo);
                                                      }
                                                    }}
                                                  >
                                                    {task.taskId}
                                                  </span>
                                                </span>
                                                {renderAgentPopup(task, "taskId")}

                                              </div>
                                            )
                                              : header?.value === "taskStatus" ? (
                                                <span
                                                  className={`text-[11px] px-2 py-1 font-semibold rounded-md border border-current/15 uppercase tracking[0.10em] ${task.taskStatus === "Active"
                                                    ? `text-[#f2930d] bg-[#fcf2e3]`
                                                    : ""
                                                    } ${task.taskStatus === "Assigned"
                                                      ? `text-[#3571e9] bg-[#e9effb]`
                                                      : ""
                                                    } ${task.taskStatus === "On_Hold"
                                                      ? `text-[#EF4A00] bg-[#EF4A0033]`
                                                      : ""
                                                    } ${task.taskStatus === "Completed"
                                                      ? `text-[#8541c8] bg-[#f2ecf9]`
                                                      : ""
                                                    } ${task.taskStatus === "In_Progress"
                                                      ? `text-[#FF9F29] bg-[#FF9F2933]`
                                                      : ""
                                                    } ${task.taskStatus === "Pending"
                                                      ? `text-[#838995] bg-[#eeeff1]`
                                                      : ""
                                                    } ${task.taskStatus === "Finished"
                                                      ? `text-[#29a36a] bg-[#e8f7f0]`
                                                      : ""
                                                    } inline-flex items-center gap-1.5`}
                                                >
                                                  {task.taskStatus === "Finished" && <CircleCheck className="shrink-0" size={16} />}
                                                  {task.taskStatus === "Assigned" && <UserCheck className="shrink-0" size={16} />}
                                                  {task.taskStatus === "Completed" && <CircleDot className="shrink-0" size={16} />}
                                                  {task.taskStatus === "Active" && <Zap className="shrink-0" size={16} />}
                                                  {task.taskStatus === "In_Progress" && <Play className="shrink-0" size={16} />}
                                                  {task.taskStatus === "On_Hold" && <PauseCircle className="shrink-0" size={16} />}
                                                  {task.taskStatus === "Pending" && <Clock4 className="shrink-0" size={16} />}
                                                  {task.taskStatus.replace("_", " ")}
                                                </span>
                                              )
                                                : header?.value === "priority" ? (
                                                  <PriorityDropdown task={task} updateTaskPriorityHandler={updateTaskPriorityHandler} isDisabled={!canUpdateTask} />
                                                ) : header?.value === "source" ? (
                                                  <span
                                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold !rounded-md border border-current/15 tracking-[0.08em] uppercase ${(task.source || "").toUpperCase() === "AUTOMATED"
                                                      ? "bg-[#f0ecf9] text-[#6a41c8]"
                                                      : (task.source || "").toUpperCase() === "IMPORTED"
                                                        ? "bg-[#e8f4fd] text-[#1d6dc2]"
                                                        : "bg-[#e7f7f8] text-[#2295a0]"
                                                      }`}
                                                  >
                                                    {(task.source || "").toUpperCase() === "AUTOMATED" && <Bot className="shrink-0" size={15} />}
                                                    {(task.source || "").toUpperCase() === "MANUAL" && <Hand className="shrink-0" size={15} />}
                                                    {(task.source || "").toUpperCase() === "IMPORTED" && <Upload className="shrink-0" size={15} />}
                                                    {task.source}
                                                  </span>
                                                )
                                                  : header?.value === "assignedTo" ? (
                                                      <div
                                                        /* w-fit keeps the hover trigger hugging the avatars/names instead of
                                                           the whole column, so the popup opens right next to them. */
                                                        className="relative inline-flex w-fit max-w-full cursor-pointer"
                                                        onMouseEnter={(e) => handleMouseEnter(task.taskId, e, "assignedTo")}
                                                        onMouseLeave={handleMouseLeave}
                                                        onClick={(e) => handleCapIconClick(task.taskId, e, "assignedTo")}
                                                      >
                                                        <AssigneeAvatarStack assignees={task.assignees} langMode={langMode} />
                                                        {renderAgentPopup(task, "assignedTo")}
                                                      </div>
                                                    )
                                                    : header?.value === "Comment" ? (
                                                      <span className="block px-2 py-1">
                                                        <OverflowTooltipText text={task?.comment !== null ? task?.comment : "-"} />
                                                      </span>
                                                    ) :
                                                      header?.value === "cycle" ? (
                                                        /* Cycle is a top-level field on the task, like taskStatus or
                                                           source. The fallback at the end of this chain only reads
                                                           taskDetails, so without a branch here the value list/search
                                                           returns rendered as an empty cell. */
                                                        <OverflowTooltipText text={task?.cycle || "-"} />
                                                      ) :
                                                      (
                                                        header?.value === "taskCategory" ? (
                                                          <OverflowTooltipText text={Array.isArray(task.taskCategory) ? task.taskCategory.join(' , ') : task.taskCategory} />
                                                        ) : (
                                                          header?.value === 'Activity' ?
                                                            <span
                                                              className="inline-block max-w-full px-2 py-1 rounded-lg bg-[#edeef2] text-[11px] font-semibold text-[#70788f] leading-snug break-words whitespace-normal"
                                                              title={taskDetailsObj?.Activity || ""}
                                                            >
                                                              {taskDetailsObj?.Activity || "-"}
                                                            </span>
                                                            : <OverflowTooltipText text={taskDetailsObj[header?.value]} />
                                                        )
                                                      )}
                                  </td>
                                ))}
                                {showRightFillerColumn && <td aria-hidden="true" className="w-auto p-0" />}
                              </tr>
                            );
                          })}
                            <tr aria-hidden="true" className="pointer-events-none">
                              <td
                                colSpan={totalTableColumnsCount}
                                className="h-4 border-0 p-0 bg-transparent"
                              />
                            </tr>
                          </>
                        )}
                </Fragment>
              )}
            </tbody>
          </table>
          {showHiddenColumnsPlaceholder && showRightFillerColumn && (
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-[4] hidden items-center justify-center px-3 sm:flex"
              style={{ width: `${placeholderWidthPx}px` }}
            >
              <HiddenColumnsPlaceholder theme={theme} />
            </div>
          )}
          {showHiddenColumnsChip && (
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-[4] hidden items-center justify-center sm:flex"
              style={{ width: `${placeholderWidthPx}px` }}
            >
              <HiddenColumnsChip theme={theme} />
            </div>
          )}
        </div>
        {open && (
          <Modal pdfLink={selectedPdfLink} setOpen={setOpen} open={open} />
        )}
        {(accountHistory !== null || isLoading) &&
          <Transition.Root show={true} as={Fragment}>
            <Dialog
              as="div"
              className="relative z-50"
              onClose={() => setAccountHistory(null)}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" />
              </Transition.Child>
              <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-2 sm:p-6">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="relative w-full max-w-4xl max-h-[calc(100dvh-1rem)] sm:max-h-[90vh] flex flex-col rounded-lg bg-white shadow-2xl ring-1 ring-slate-200/80 overflow-hidden">
                      {/* Header */}
                      <div className="flex items-center justify-between shrink-0 px-3 py-3 sm:px-6 sm:py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600">
                            <History className="h-5 w-5" />
                          </div>
                          <div>
                            <Dialog.Title className="text-lg font-semibold text-slate-800">{translations[langMode]?.acchistory ?? "Account History"}</Dialog.Title>
                            <p className="text-xs text-slate-500 mt-0.5">{translations[langMode].accDetail}</p>
                          </div>
                        </div>
                        <ModalCloseButton onClick={() => setAccountHistory(null)} ariaLabel={translations[langMode]?.bulkExportClose ?? "Close"} />
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1 overflow-y-auto p-3 space-y-4 sm:p-6 sm:space-y-6">
                        {isLoading ? (
                          <div className="flex flex-col items-center justify-center gap-4 py-16">
                            <div className="h-12 w-12 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin" />
                            <p className="text-sm font-medium text-slate-600">{translations[langMode].fetchingaccdetails}</p>
                          </div>
                        ) : (
                          <>
                            {(accountHistory || []).length === 0 && (
                              <p className="py-10 text-center text-sm text-slate-500">{translations[langMode]?.noAccountDetialsAvailable ?? "No account details available"}</p>
                            )}
                            {(accountHistory || []).map((department, departmentIndex) => (
                              <div key={department.departmentId ?? departmentIndex} className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
                                {department.departmentName && (
                                  <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border-b border-slate-200">
                                    <Building2 className="h-4 w-4 text-indigo-600" />
                                    <span className="text-sm font-semibold text-slate-800">{department.departmentName}</span>
                                  </div>
                                )}
                                <div className="space-y-4 p-3 sm:p-4">
                                  {department.projects.map((project, projectIndex) => {
                                    const accountDetails = project.details;
                                    return (
                                      <div key={project.projectId ?? projectIndex} className={project.projectName ? "rounded-md border-l-4 border-indigo-500 bg-slate-50/60 p-3 space-y-4" : "space-y-4"}>
                                        {project.projectName && (
                                          <div className="flex items-center gap-2">
                                            <FolderOpen className="h-4 w-4 text-indigo-600" />
                                            <span className="text-sm font-semibold text-indigo-800">{project.projectName}</span>
                                          </div>
                                        )}

                                        {/* Account details card */}
                                        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                                          <div className="px-4 py-3 border-b border-slate-200 bg-white">
                                            <h3 className="text-sm font-semibold text-slate-700">{translations[langMode].accDetail}</h3>
                                          </div>
                                          {accountDetails ? (
                                            <div className="overflow-x-auto">
                                              <table className="w-full min-w-[500px] text-sm">
                                                <thead>
                                                  <tr className="bg-slate-100/80">
                                                    {Object.entries(accountDetails.accountDetailsJson || {}).map(([key]) => (
                                                      <th key={key} className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{key.trim()}</th>
                                                    ))}
                                                    <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].curros}</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].lastupdateddate}</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].lastupdatedtime}</th>
                                                    <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].currenOs} {translations[langMode].updatedat}</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  <tr className="border-t border-slate-200 bg-white hover:bg-slate-50/50 transition-colors">
                                                    {Object.entries(accountDetails.accountDetailsJson || {}).map(([key, value]) => (
                                                      <td key={key} className="px-4 py-3 text-slate-600 whitespace-nowrap">{value}</td>
                                                    ))}
                                                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{accountDetails.currentOs || "-"}</td>
                                                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{accountDetails.lastUpdatedDate || "-"}</td>
                                                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{accountDetails.lastUpdatedTime || "-"}</td>
                                                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{accountDetails.currentOsUpdatedAt ? moment.utc(accountDetails.currentOsUpdatedAt).format("DD-MM-YYYY HH:mm") : "-"}</td>
                                                  </tr>
                                                </tbody>
                                              </table>
                                            </div>
                                          ) : (
                                            <p className="px-4 py-4 text-sm text-slate-500">{translations[langMode]?.accHistNoProjectDetails ?? "No account details in this project."}</p>
                                          )}
                                        </div>

                                        {/* Task details section */}
                                        {project.workflows.length > 0 && (
                                          <div>
                                            <h3 className="text-sm font-semibold text-slate-700 mb-3">{translations[langMode].taskdetail}</h3>
                                            <div className="space-y-4">
                                              {project.workflows.map((workflow, index) => (
                                                <div key={index} className="rounded-lg border border-slate-200 bg-white overflow-hidden shadow-sm">
                                                  <div className="px-4 py-2.5 bg-indigo-50/70 border-b border-slate-200">
                                                    <span className="text-sm font-medium text-indigo-800">{workflow.workflowName}</span>
                                                  </div>
                                                  <div className="overflow-x-auto">
                                                    <table className="w-full min-w-[700px] text-sm account-history">
                                                      <thead>
                                                        <tr className="bg-slate-100/80">
                                                          <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].taskId}</th>
                                                          <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].assingename}</th>
                                                          <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].finisheddate}</th>
                                                          <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].fnishedtime}</th>
                                                          <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].workflowid}</th>
                                                          <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].pdflink}</th>
                                                          <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].createBy}</th>
                                                          <th className="px-4 py-3 text-left font-semibold text-slate-700 whitespace-nowrap">{translations[langMode].remark}</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody className="text-slate-600">
                                                        {workflow.accountHistoryList?.map((entry, idx) => (
                                                          <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                                                            <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-700">{entry.taskId}</td>
                                                            <td className="px-4 py-3 whitespace-nowrap">{entry.assigneeName}</td>
                                                            <td className="px-4 py-3 whitespace-nowrap">{entry.finishedDate}</td>
                                                            <td className="px-4 py-3 whitespace-nowrap">{entry.finishedTime}</td>
                                                            <td className="px-4 py-3 whitespace-nowrap">{entry.workflowId}</td>
                                                            <td className="px-4 py-3">
                                                              <ActionIconButton
                                                                type="button"
                                                                onClick={() => handleOpenPDF(entry.taskId)}
                                                                tone="blue"
                                                                className="h-8 w-8 border-transparent text-slate-500 hover:text-indigo-600"
                                                                title={translations[langMode].pdflink}
                                                                aria-label={translations[langMode].pdflink ?? "Open PDF"}
                                                              >
                                                                <img src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344905/fileTypeIcon_mmuvfk.png" className="w-5 h-5" alt="" />
                                                              </ActionIconButton>
                                                            </td>
                                                            <td className="px-4 py-3 whitespace-nowrap">{entry.createdBy || "-"}</td>
                                                            <td className="px-4 py-3 max-w-[180px] truncate" title={entry.remarks || ""}>{entry.remarks || "-"}</td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </Dialog>
          </Transition.Root>
        }
        {selectedTaskForSlider && (
          <div className="fixed inset-0 z-[100] flex justify-end">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
              onClick={() => setSelectedTaskForSlider(null)}
            />

            {/* Slider panel */}
            <div className="relative w-full sm:w-[85%] md:w-[75%] lg:w-[65%] xl:w-[60%] bg-slate-50 shadow-2xl h-full flex flex-col z-10 animate-slide-in-right overflow-y-auto">
              <TaskDetailPage
                key={selectedTaskForSlider.taskId}
                taskId={selectedTaskForSlider.taskId}
                accountNo={selectedTaskForSlider.accountNo}
                onClose={() => setSelectedTaskForSlider(null)}
              />
            </div>
          </div>
        )}
        {hasAIChatAgentAccess?.canView && <AgentChatWidget />}
        <AlertModal {...alertProps} />
        {taskLocationView && (
          <LocationMapModal
            location={taskLocationView}
            title={translations[langMode]?.taskLocationTitle ?? "Task Location"}
            onClose={() => setTaskLocationView(null)}
            t={translations[langMode]}
            isRtl={langMode === "ar" || langMode === "ur"}
          />
        )}
      </div>
    </>
  );
};

export default TaskListView;
